import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, Plus, Search, Edit, Trash2, Check, Loader2,
  Calendar, User, Clock, AlertCircle, CheckCircle, ChevronDown,
  MessageSquare, Briefcase, FileText, DollarSign, Scale, Cake,
  X, Eye, ArrowRight, ChevronRight, ListTodo, ChevronUp,
  Phone, Mail, Send, RefreshCw, Paperclip, Upload, GitBranch, 
  History, FolderPlus, ExternalLink, BarChart3, Globe, Instagram
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { tareasDespachoAPI, usuariosAPI, atencionClienteAPI } from '../api';
import { toast } from '../hooks/use-toast';
import ExpedienteSearch from '../components/ExpedienteSearch';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES');
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('es-ES');
};

// Configuración de categorías
const CATEGORIAS_CONFIG = {
  abogados: {
    id: 'abogados',
    nombre: 'Tarea Abogados',
    icon: Briefcase,
    color: 'blue',
    bgColor: 'bg-blue-600',
    lightBg: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-l-blue-600'
  },
  minutas: {
    id: 'minutas',
    nombre: 'Tarea Minutas',
    icon: FileText,
    color: 'green',
    bgColor: 'bg-green-600',
    lightBg: 'bg-green-50',
    textColor: 'text-green-700',
    borderColor: 'border-l-green-600'
  },
  costas_pagar: {
    id: 'costas_pagar',
    nombre: 'Costas a Pagar',
    icon: DollarSign,
    color: 'orange',
    bgColor: 'bg-orange-600',
    lightBg: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-l-orange-600'
  },
  ejecuciones: {
    id: 'ejecuciones',
    nombre: 'Ejecuciones',
    icon: Scale,
    color: 'purple',
    bgColor: 'bg-purple-600',
    lightBg: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-l-purple-600'
  },
  pastelitos: {
    id: 'pastelitos',
    nombre: 'Pastelitos',
    icon: Cake,
    color: 'pink',
    bgColor: 'bg-pink-600',
    lightBg: 'bg-pink-50',
    textColor: 'text-pink-700',
    borderColor: 'border-l-pink-600'
  },
  atencion_cliente: {
    id: 'atencion_cliente',
    nombre: 'Atención Cliente',
    icon: MessageSquare,
    color: 'cyan',
    bgColor: 'bg-cyan-600',
    lightBg: 'bg-cyan-50',
    textColor: 'text-cyan-700',
    borderColor: 'border-l-cyan-600'
  }
};

const PRIORIDADES = {
  baja: { nombre: 'Baja', color: 'bg-gray-100 text-gray-700' },
  media: { nombre: 'Media', color: 'bg-blue-100 text-blue-700' },
  alta: { nombre: 'Alta', color: 'bg-orange-100 text-orange-700' },
  urgente: { nombre: 'Urgente', color: 'bg-red-100 text-red-700 animate-pulse' }
};

const FASES_MINUTAS = {
  fase_a: { nombre: 'Fase A - Solicitud', color: 'bg-yellow-100 text-yellow-800', icon: '📝' },
  fase_b: { nombre: 'Fase B - Tramitación', color: 'bg-blue-100 text-blue-800', icon: '📤' },
  fase_c: { nombre: 'Fase C - Finalización', color: 'bg-green-100 text-green-800', icon: '✅' }
};

const TareasDespacho = () => {
  const [activeTab, setActiveTab] = useState('abogados');
  const [tareas, setTareas] = useState([]);
  const [stats, setStats] = useState({ por_categoria: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCompletada, setFilterCompletada] = useState('pendientes');
  const [filterPrioridad, setFilterPrioridad] = useState('');
  
  // Data para selects
  const [usuarios, setUsuarios] = useState([]);
  
  // Dialogs
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [selectedTarea, setSelectedTarea] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadTareas();
  }, [activeTab, filterCompletada, filterPrioridad]);

  const loadInitialData = async () => {
    try {
      const [statsRes, usuariosRes] = await Promise.all([
        tareasDespachoAPI.getStats(),
        usuariosAPI.getAll()
      ]);
      
      setStats(statsRes.data);
      setUsuarios(usuariosRes.data);
    } catch (error) {
      console.error('Error cargando datos iniciales:', error);
    }
  };

  const loadTareas = async () => {
    try {
      setLoading(true);
      const params = { search: search || undefined };
      
      if (filterCompletada === 'pendientes') {
        params.completada = false;
      } else if (filterCompletada === 'completadas') {
        params.completada = true;
      }
      
      if (filterPrioridad) {
        params.prioridad = filterPrioridad;
      }
      
      let tareasRes;
      switch (activeTab) {
        case 'abogados':
          tareasRes = await tareasDespachoAPI.abogados.getAll(params);
          break;
        case 'minutas':
          // Para minutas, filtrar por fase si no está completada
          if (filterCompletada === 'pendientes') {
            delete params.completada;
          }
          tareasRes = await tareasDespachoAPI.minutas.getAll(params);
          break;
        case 'costas_pagar':
          tareasRes = await tareasDespachoAPI.costas.getAll(params);
          break;
        case 'ejecuciones':
          tareasRes = await tareasDespachoAPI.ejecuciones.getAll(params);
          break;
        case 'pastelitos':
          tareasRes = await tareasDespachoAPI.pastelitos.getAll(params);
          break;
        default:
          tareasRes = { data: [] };
      }
      
      setTareas(tareasRes.data);
      
      // Actualizar estadísticas
      const statsRes = await tareasDespachoAPI.getStats();
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error cargando tareas:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las tareas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tarea) => {
    if (!window.confirm('¿Eliminar esta tarea?')) return;
    
    try {
      switch (activeTab) {
        case 'abogados':
          await tareasDespachoAPI.abogados.delete(tarea.id);
          break;
        case 'minutas':
          await tareasDespachoAPI.minutas.delete(tarea.id);
          break;
        case 'costas_pagar':
          await tareasDespachoAPI.costas.delete(tarea.id);
          break;
        case 'ejecuciones':
          await tareasDespachoAPI.ejecuciones.delete(tarea.id);
          break;
        case 'pastelitos':
          await tareasDespachoAPI.pastelitos.delete(tarea.id);
          break;
      }
      toast({ title: 'Tarea eliminada' });
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  const handleToggleCompletada = async (tarea) => {
    try {
      const nuevoEstado = !tarea.completada;
      switch (activeTab) {
        case 'abogados':
          await tareasDespachoAPI.abogados.completar(tarea.id, nuevoEstado);
          break;
        case 'costas_pagar':
          await tareasDespachoAPI.costas.completar(tarea.id, nuevoEstado);
          break;
        case 'ejecuciones':
          await tareasDespachoAPI.ejecuciones.completar(tarea.id, nuevoEstado);
          break;
        case 'pastelitos':
          await tareasDespachoAPI.pastelitos.completar(tarea.id, nuevoEstado);
          break;
      }
      toast({ 
        title: nuevoEstado ? 'Tarea completada' : 'Tarea marcada como pendiente'
      });
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const currentConfig = CATEGORIAS_CONFIG[activeTab];
  const CurrentIcon = currentConfig.icon;

  // Calcular estadísticas de la categoría actual
  const categoriaStats = stats.por_categoria?.find(c => c.categoria === activeTab) || { total: 0, pendientes: 0 };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tareas del Despacho</h1>
            <p className="text-gray-600">Gestión de tareas por categoría</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          onClick={() => window.location.href = '/tareas/estadisticas'}
          className="gap-2"
        >
          <BarChart3 className="w-4 h-4" />
          Ver Estadísticas
        </Button>
      </div>

      {/* Stats generales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.values(CATEGORIAS_CONFIG).map((cat) => {
          const catStats = stats.por_categoria?.find(c => c.categoria === cat.id);
          const Icon = cat.icon;
          return (
            <Card 
              key={cat.id} 
              className={`cursor-pointer transition-all hover:shadow-md border-l-4 ${cat.borderColor} ${activeTab === cat.id ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
              onClick={() => setActiveTab(cat.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-5 h-5 ${cat.textColor}`} />
                  <span className="text-sm font-medium text-gray-700">{cat.nombre}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold">{catStats?.pendientes || 0}</span>
                  <span className="text-sm text-gray-500">/ {catStats?.total || 0}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs de categorías */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6 h-auto">
          {Object.values(CATEGORIAS_CONFIG).map((cat) => {
            const Icon = cat.icon;
            const catStats = stats.por_categoria?.find(c => c.categoria === cat.id);
            return (
              <TabsTrigger 
                key={cat.id} 
                value={cat.id}
                className="flex items-center gap-2 py-3"
              >
                <Icon className="w-4 h-4" />
                <span className="hidden lg:inline">{cat.nombre}</span>
                {catStats?.pendientes > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {catStats.pendientes}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* ===================== TAB ABOGADOS ===================== */}
        <TabsContent value="abogados" className="mt-4 space-y-4">
          <TareaAbogadosTab 
            tareas={tareas}
            loading={loading}
            search={search}
            setSearch={setSearch}
            filterCompletada={filterCompletada}
            setFilterCompletada={setFilterCompletada}
            filterPrioridad={filterPrioridad}
            setFilterPrioridad={setFilterPrioridad}
            loadTareas={loadTareas}
            handleDelete={handleDelete}
            handleToggleCompletada={handleToggleCompletada}
            usuarios={usuarios}
            config={CATEGORIAS_CONFIG.abogados}
          />
        </TabsContent>

        {/* ===================== TAB MINUTAS ===================== */}
        <TabsContent value="minutas" className="mt-4 space-y-4">
          <TareaMinutasTab 
            tareas={tareas}
            loading={loading}
            search={search}
            setSearch={setSearch}
            filterCompletada={filterCompletada}
            setFilterCompletada={setFilterCompletada}
            filterPrioridad={filterPrioridad}
            setFilterPrioridad={setFilterPrioridad}
            loadTareas={loadTareas}
            handleDelete={handleDelete}
            usuarios={usuarios}
            config={CATEGORIAS_CONFIG.minutas}
          />
        </TabsContent>

        {/* ===================== TAB COSTAS ===================== */}
        <TabsContent value="costas_pagar" className="mt-4 space-y-4">
          <TareaCostasTab 
            tareas={tareas}
            loading={loading}
            search={search}
            setSearch={setSearch}
            filterCompletada={filterCompletada}
            setFilterCompletada={setFilterCompletada}
            filterPrioridad={filterPrioridad}
            setFilterPrioridad={setFilterPrioridad}
            loadTareas={loadTareas}
            handleDelete={handleDelete}
            handleToggleCompletada={handleToggleCompletada}
            usuarios={usuarios}
            config={CATEGORIAS_CONFIG.costas_pagar}
          />
        </TabsContent>

        {/* ===================== TAB EJECUCIONES ===================== */}
        <TabsContent value="ejecuciones" className="mt-4 space-y-4">
          <TareaEjecucionesTab 
            tareas={tareas}
            loading={loading}
            search={search}
            setSearch={setSearch}
            filterCompletada={filterCompletada}
            setFilterCompletada={setFilterCompletada}
            filterPrioridad={filterPrioridad}
            setFilterPrioridad={setFilterPrioridad}
            loadTareas={loadTareas}
            handleDelete={handleDelete}
            handleToggleCompletada={handleToggleCompletada}
            usuarios={usuarios}
            config={CATEGORIAS_CONFIG.ejecuciones}
          />
        </TabsContent>

        {/* ===================== TAB PASTELITOS ===================== */}
        <TabsContent value="pastelitos" className="mt-4 space-y-4">
          <TareaPastelitosTab 
            tareas={tareas}
            loading={loading}
            search={search}
            setSearch={setSearch}
            filterCompletada={filterCompletada}
            setFilterCompletada={setFilterCompletada}
            filterPrioridad={filterPrioridad}
            setFilterPrioridad={setFilterPrioridad}
            loadTareas={loadTareas}
            handleDelete={handleDelete}
            handleToggleCompletada={handleToggleCompletada}
            usuarios={usuarios}
            config={CATEGORIAS_CONFIG.pastelitos}
          />
        </TabsContent>

        {/* ===================== TAB ATENCIÓN CLIENTE ===================== */}
        <TabsContent value="atencion_cliente" className="mt-4 space-y-4">
          <AtencionClienteTab 
            usuarios={usuarios}
            config={CATEGORIAS_CONFIG.atencion_cliente}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============================================================================
// COMPONENTE: TAREA ABOGADOS
// ============================================================================
const TareaAbogadosTab = ({ tareas, loading, search, setSearch, filterCompletada, setFilterCompletada, filterPrioridad, setFilterPrioridad, loadTareas, handleDelete, handleToggleCompletada, usuarios, config }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [expandedSubtareas, setExpandedSubtareas] = useState({});
  const [form, setForm] = useState({
    descripcion: '',
    fecha_limite: '',
    asignado_a_id: '',
    asignado_a_nombre: '',
    prioridad: 'media'
  });

  const toggleSubtareas = (tareaId) => {
    setExpandedSubtareas(prev => ({ ...prev, [tareaId]: !prev[tareaId] }));
  };

  const resetForm = () => {
    setForm({
      descripcion: '',
      fecha_limite: '',
      asignado_a_id: '',
      asignado_a_nombre: '',
      prioridad: 'media'
    });
    setSelectedExpediente(null);
    setEditingTarea(null);
  };

  const handleOpenNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (tarea) => {
    setForm({
      descripcion: tarea.descripcion || '',
      fecha_limite: tarea.fecha_limite || '',
      asignado_a_id: tarea.asignado_a_id?.toString() || '',
      asignado_a_nombre: tarea.asignado_a_nombre || '',
      prioridad: tarea.prioridad || 'media'
    });
    setSelectedExpediente(tarea.expediente_id ? {
      id: tarea.expediente_id,
      numero_expediente: tarea.expediente_numero
    } : null);
    setEditingTarea(tarea);
    setIsDialogOpen(true);
  };

  const handleUsuarioChange = (userId) => {
    const user = usuarios.find(u => u.id.toString() === userId);
    setForm({
      ...form,
      asignado_a_id: userId,
      asignado_a_nombre: user?.nombre || user?.email || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.descripcion) {
      toast({ title: 'Error', description: 'La descripción es obligatoria', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        expediente_id: selectedExpediente?.id || null,
        expediente_numero: selectedExpediente?.numero_expediente || null,
        asignado_a_id: form.asignado_a_id ? parseInt(form.asignado_a_id) : null,
        fecha_limite: form.fecha_limite || null
      };
      
      if (editingTarea) {
        await tareasDespachoAPI.abogados.update(editingTarea.id, data);
        toast({ title: 'Tarea actualizada' });
      } else {
        await tareasDespachoAPI.abogados.create(data);
        toast({ title: 'Tarea creada' });
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const Icon = config.icon;

  return (
    <>
      {/* Header de categoría */}
      <Card className={`${config.lightBg} border-l-4 ${config.borderColor}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{config.nombre}</h2>
                <p className="text-sm text-gray-600">Tareas simples asignadas a abogados</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros y acciones */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar tareas..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadTareas()}
                />
              </div>
            </div>
            
            <Select value={filterCompletada} onValueChange={setFilterCompletada}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="pendientes">Pendientes</SelectItem>
                <SelectItem value="completadas">Completadas</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterPrioridad || 'all'} onValueChange={(v) => setFilterPrioridad(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Prioridad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {Object.entries(PRIORIDADES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button className={`${config.bgColor} hover:opacity-90 text-white`} onClick={handleOpenNew}>
              <Plus className="w-4 h-4 mr-2" />
              NUEVA TAREA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de tareas */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : tareas.length > 0 ? (
            <div className="divide-y">
              {tareas.map((tarea) => {
                const isVencida = tarea.fecha_limite && new Date(tarea.fecha_limite) < new Date() && !tarea.completada;
                
                return (
                  <div 
                    key={tarea.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${tarea.completada ? 'bg-gray-50 opacity-75' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      <button 
                        onClick={() => handleToggleCompletada(tarea)}
                        className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          tarea.completada 
                            ? 'bg-green-500 border-green-500 text-white' 
                            : 'border-gray-300 hover:border-green-500'
                        }`}
                      >
                        {tarea.completada && <Check className="w-4 h-4" />}
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className={`font-medium ${tarea.completada ? 'line-through text-gray-500' : ''}`}>
                              {tarea.descripcion}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button 
                              className="p-1 hover:bg-gray-200 rounded"
                              onClick={() => handleEdit(tarea)}
                              title="Editar"
                            >
                              <Edit className="w-4 h-4 text-gray-600" />
                            </button>
                            <button 
                              className="p-1 hover:bg-gray-200 rounded"
                              onClick={() => handleDelete(tarea)}
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                          <Badge className={PRIORIDADES[tarea.prioridad]?.color || ''}>
                            {PRIORIDADES[tarea.prioridad]?.nombre || tarea.prioridad}
                          </Badge>
                          
                          {tarea.asignado_a_nombre && (
                            <span className="flex items-center gap-1 text-gray-600">
                              <User className="w-3 h-3" />
                              {tarea.asignado_a_nombre}
                            </span>
                          )}
                          
                          {tarea.fecha_limite && (
                            <span className={`flex items-center gap-1 ${isVencida ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                              <Calendar className="w-3 h-3" />
                              {formatDate(tarea.fecha_limite)}
                              {isVencida && <AlertCircle className="w-3 h-3" />}
                            </span>
                          )}
                          
                          {tarea.expediente_numero && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <FileText className="w-3 h-3" />
                              {tarea.expediente_numero}
                            </span>
                          )}
                          
                          {tarea.completada && tarea.fecha_completada && (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-3 h-3" />
                              Completada: {formatDateTime(tarea.fecha_completada)}
                            </span>
                          )}
                        </div>

                        {/* Sub-tareas */}
                        <SubTareasSection 
                          tipoTarea="abogados"
                          tareaId={tarea.id}
                          isExpanded={expandedSubtareas[tarea.id] || false}
                          onToggle={() => toggleSubtareas(tarea.id)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay tareas en esta categoría</p>
              <Button variant="outline" className="mt-4" onClick={handleOpenNew}>
                <Plus className="w-4 h-4 mr-2" />
                Crear primera tarea
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nueva/Editar */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              {editingTarea ? 'Editar Tarea Abogados' : 'Nueva Tarea Abogados'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label>Expediente relacionado</Label>
                <ExpedienteSearch
                  value={selectedExpediente}
                  onChange={setSelectedExpediente}
                  placeholder="Buscar expediente por número o título..."
                />
              </div>
              
              <div>
                <Label>Descripción de la tarea *</Label>
                <Textarea 
                  value={form.descripcion}
                  onChange={(e) => setForm({...form, descripcion: e.target.value})}
                  placeholder="Describe la tarea a realizar..."
                  rows={3}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha límite</Label>
                  <Input 
                    type="date"
                    value={form.fecha_limite}
                    onChange={(e) => setForm({...form, fecha_limite: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Prioridad</Label>
                  <Select value={form.prioridad} onValueChange={(v) => setForm({...form, prioridad: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORIDADES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Asignar a</Label>
                <Select value={form.asignado_a_id} onValueChange={handleUsuarioChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar abogado" />
                  </SelectTrigger>
                  <SelectContent>
                    {usuarios.map(u => (
                      <SelectItem key={u.id} value={u.id.toString()}>
                        {u.nombre || u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className={`${config.bgColor} hover:opacity-90 text-white`} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingTarea ? 'Guardar' : 'Crear Tarea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============================================================================
// COMPONENTE: TAREA MINUTAS (Con workflow de 3 fases)
// ============================================================================
const TareaMinutasTab = ({ tareas, loading, search, setSearch, filterCompletada, setFilterCompletada, filterPrioridad, setFilterPrioridad, loadTareas, handleDelete, usuarios, config }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedMinuta, setSelectedMinuta] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterFase, setFilterFase] = useState('');
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [form, setForm] = useState({
    descripcion_tarea: '',
    fecha_limite_fase_a: '',
    asignado_a_id: '',
    asignado_a_nombre: ''
  });

  const resetForm = () => {
    setForm({
      descripcion_tarea: '',
      fecha_limite_fase_a: '',
      asignado_a_id: '',
      asignado_a_nombre: ''
    });
    setSelectedExpediente(null);
  };

  const handleUsuarioChange = (userId) => {
    const user = usuarios.find(u => u.id.toString() === userId);
    setForm({
      ...form,
      asignado_a_id: userId,
      asignado_a_nombre: user?.nombre || user?.email || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSaving(true);
    try {
      const data = {
        ...form,
        expediente_id: selectedExpediente?.id || null,
        expediente_numero: selectedExpediente?.numero_expediente || null,
        asignado_a_id: form.asignado_a_id ? parseInt(form.asignado_a_id) : null,
        fecha_limite_fase_a: form.fecha_limite_fase_a || null
      };
      
      await tareasDespachoAPI.minutas.create(data);
      toast({ title: 'Minuta creada en Fase A' });
      
      setIsDialogOpen(false);
      resetForm();
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo crear', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetail = (minuta) => {
    setSelectedMinuta(minuta);
    setIsDetailOpen(true);
  };

  const handleAvanzarFase = async (minuta, fase) => {
    try {
      if (fase === 'fase_a') {
        await tareasDespachoAPI.minutas.updateFaseA(minuta.id, { fase_a_completada: true });
        toast({ title: 'Fase A completada, avanzando a Fase B' });
      } else if (fase === 'fase_b') {
        await tareasDespachoAPI.minutas.updateFaseB(minuta.id, { fase_b_completada: true });
        toast({ title: 'Fase B completada, avanzando a Fase C' });
      } else if (fase === 'fase_c') {
        await tareasDespachoAPI.minutas.updateFaseC(minuta.id, { fase_c_completada: true });
        toast({ title: 'Minuta finalizada con número asignado' });
      }
      loadTareas();
      setIsDetailOpen(false);
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo avanzar la fase', variant: 'destructive' });
    }
  };

  const Icon = config.icon;

  // Filtrar por fase
  const tareasFiltradas = filterFase 
    ? tareas.filter(t => t.fase_actual === filterFase)
    : tareas;

  return (
    <>
      {/* Header de categoría */}
      <Card className={`${config.lightBg} border-l-4 ${config.borderColor}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{config.nombre}</h2>
                <p className="text-sm text-gray-600">Workflow de 3 fases: Solicitud → Tramitación → Finalización</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Indicadores de fases */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(FASES_MINUTAS).map(([key, fase]) => {
          const count = tareas.filter(t => t.fase_actual === key && !t.fase_c_completada).length;
          return (
            <Card 
              key={key} 
              className={`cursor-pointer transition-all hover:shadow-md ${filterFase === key ? 'ring-2 ring-green-500' : ''}`}
              onClick={() => setFilterFase(filterFase === key ? '' : key)}
            >
              <CardContent className="p-4 text-center">
                <div className="text-2xl mb-1">{fase.icon}</div>
                <p className="font-medium text-sm">{fase.nombre}</p>
                <p className="text-2xl font-bold text-green-600">{count}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtros y acciones */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar minutas..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadTareas()}
                />
              </div>
            </div>
            
            <Select value={filterFase || 'all'} onValueChange={(v) => setFilterFase(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrar por fase" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las fases</SelectItem>
                {Object.entries(FASES_MINUTAS).map(([key, fase]) => (
                  <SelectItem key={key} value={key}>{fase.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button className={`${config.bgColor} hover:opacity-90 text-white`} onClick={() => setIsDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              NUEVA MINUTA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de minutas */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : tareasFiltradas.length > 0 ? (
            <div className="divide-y">
              {tareasFiltradas.map((minuta) => {
                const faseInfo = FASES_MINUTAS[minuta.fase_actual];
                const isCompleta = minuta.fase_c_completada;
                
                return (
                  <div 
                    key={minuta.id}
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${isCompleta ? 'bg-green-50' : ''}`}
                    onClick={() => handleViewDetail(minuta)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={faseInfo?.color}>
                            {faseInfo?.icon} {faseInfo?.nombre}
                          </Badge>
                          {minuta.prioridad === 'urgente' && (
                            <Badge className="bg-red-100 text-red-700 animate-pulse">🚨 Urgente</Badge>
                          )}
                          {isCompleta && (
                            <Badge className="bg-green-500 text-white">
                              ✅ Nº {minuta.numero_minuta}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="font-medium">
                          {minuta.descripcion_tarea || 'Sin descripción'}
                        </p>
                        
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-600">
                          {minuta.expediente_numero && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <FileText className="w-3 h-3" />
                              {minuta.expediente_numero}
                            </span>
                          )}
                          
                          {minuta.importe_total_minuta && (
                            <span className="font-medium text-green-600">
                              €{minuta.importe_total_minuta.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                            </span>
                          )}
                          
                          {minuta.fecha_limite_fase_a && minuta.fase_actual === 'fase_a' && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Límite: {formatDate(minuta.fecha_limite_fase_a)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewDetail(minuta); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(minuta); }}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay minutas</p>
              <Button variant="outline" className="mt-4" onClick={() => setIsDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crear primera minuta
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nueva Minuta */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              Nueva Minuta (Fase A - Solicitud)
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                <p className="font-medium text-yellow-800">📝 Fase A - Solicitud</p>
                <p className="text-yellow-700">El administrativo preparará el documento según esta solicitud.</p>
              </div>
              
              <div>
                <Label>Expediente relacionado</Label>
                <ExpedienteSearch
                  value={selectedExpediente}
                  onChange={setSelectedExpediente}
                  placeholder="Buscar expediente por número o título..."
                />
              </div>
              
              <div>
                <Label>Descripción de la tarea</Label>
                <Textarea 
                  value={form.descripcion_tarea}
                  onChange={(e) => setForm({...form, descripcion_tarea: e.target.value})}
                  placeholder="Describe lo que debe preparar el administrativo..."
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha límite</Label>
                  <Input 
                    type="date"
                    value={form.fecha_limite_fase_a}
                    onChange={(e) => setForm({...form, fecha_limite_fase_a: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Asignar a</Label>
                  <Select value={form.asignado_a_id} onValueChange={handleUsuarioChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Administrativo" />
                    </SelectTrigger>
                    <SelectContent>
                      {usuarios.map(u => (
                        <SelectItem key={u.id} value={u.id.toString()}>
                          {u.nombre || u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className={`${config.bgColor} hover:opacity-90 text-white`} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Crear Minuta
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle Minuta */}
      <MinutaDetailDialog 
        minuta={selectedMinuta}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onAvanzarFase={handleAvanzarFase}
        usuarios={usuarios}
        loadTareas={loadTareas}
      />
    </>
  );
};

// ============================================================================
// DIALOG: Detalle de Minuta con gestión de fases
// ============================================================================
const MinutaDetailDialog = ({ minuta, isOpen, onClose, onAvanzarFase, usuarios, loadTareas }) => {
  const [saving, setSaving] = useState(false);
  const [formFaseB, setFormFaseB] = useState({
    fecha_presentacion: '',
    propuesta_intereses_fase_b: false,
    importe_total_minuta: '',
    observaciones_fase_b: '',
    prioridad: 'media'
  });
  const [formFaseC, setFormFaseC] = useState({
    importe_minuta_final: '',
    propuesta_intereses_fase_c: false,
    observaciones_fase_c: ''
  });

  useEffect(() => {
    if (minuta) {
      setFormFaseB({
        fecha_presentacion: minuta.fecha_presentacion || minuta.fecha_limite_fase_a || '',
        propuesta_intereses_fase_b: minuta.propuesta_intereses_fase_b || false,
        importe_total_minuta: minuta.importe_total_minuta?.toString() || '',
        observaciones_fase_b: minuta.observaciones_fase_b || '',
        prioridad: minuta.prioridad || 'media'
      });
      setFormFaseC({
        importe_minuta_final: minuta.importe_minuta_final?.toString() || minuta.importe_total_minuta?.toString() || '',
        propuesta_intereses_fase_c: minuta.propuesta_intereses_fase_c || false,
        observaciones_fase_c: minuta.observaciones_fase_c || ''
      });
    }
  }, [minuta]);

  const handleGuardarFaseB = async () => {
    setSaving(true);
    try {
      await tareasDespachoAPI.minutas.updateFaseB(minuta.id, {
        ...formFaseB,
        importe_total_minuta: formFaseB.importe_total_minuta ? parseFloat(formFaseB.importe_total_minuta) : null
      });
      toast({ title: 'Fase B actualizada' });
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleGuardarFaseC = async () => {
    setSaving(true);
    try {
      await tareasDespachoAPI.minutas.updateFaseC(minuta.id, {
        ...formFaseC,
        importe_minuta_final: formFaseC.importe_minuta_final ? parseFloat(formFaseC.importe_minuta_final) : null
      });
      toast({ title: 'Fase C actualizada' });
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!minuta) return null;

  const faseInfo = FASES_MINUTAS[minuta.fase_actual];
  const isCompleta = minuta.fase_c_completada;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Detalle de Minuta
            {isCompleta && <Badge className="bg-green-500 text-white ml-2">Nº {minuta.numero_minuta}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
          {Object.entries(FASES_MINUTAS).map(([key, fase], idx) => {
            const isActive = minuta.fase_actual === key;
            const isComplete = 
              (key === 'fase_a' && minuta.fase_a_completada) ||
              (key === 'fase_b' && minuta.fase_b_completada) ||
              (key === 'fase_c' && minuta.fase_c_completada);
            
            return (
              <React.Fragment key={key}>
                <div className={`flex flex-col items-center ${isActive ? 'scale-110' : ''}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                    isComplete ? 'bg-green-500 text-white' : 
                    isActive ? 'bg-blue-500 text-white' : 'bg-gray-200'
                  }`}>
                    {isComplete ? '✓' : fase.icon}
                  </div>
                  <span className={`text-xs mt-1 ${isActive ? 'font-bold' : ''}`}>
                    {key === 'fase_a' ? 'Solicitud' : key === 'fase_b' ? 'Tramitación' : 'Finalización'}
                  </span>
                </div>
                {idx < 2 && (
                  <ChevronRight className={`w-6 h-6 ${idx === 0 && minuta.fase_a_completada ? 'text-green-500' : idx === 1 && minuta.fase_b_completada ? 'text-green-500' : 'text-gray-300'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Información general */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-gray-500">Expediente</Label>
              <p className="font-medium">{minuta.expediente_numero || '-'}</p>
            </div>
            <div>
              <Label className="text-gray-500">Creado por</Label>
              <p className="font-medium">{minuta.creado_por_nombre || '-'}</p>
            </div>
          </div>

          {/* FASE A */}
          <Card className={minuta.fase_actual === 'fase_a' ? 'ring-2 ring-yellow-400' : ''}>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                📝 Fase A - Solicitud
                {minuta.fase_a_completada && <Badge className="bg-green-500 text-white">Completada</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-gray-500">Descripción</Label>
                <p>{minuta.descripcion_tarea || '-'}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Fecha límite</Label>
                  <p>{formatDate(minuta.fecha_limite_fase_a)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Asignado a</Label>
                  <p>{minuta.asignado_a_nombre || '-'}</p>
                </div>
              </div>
              
              {minuta.fase_actual === 'fase_a' && !minuta.fase_a_completada && (
                <Button 
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-white"
                  onClick={() => onAvanzarFase(minuta, 'fase_a')}
                >
                  Completar Fase A y Avanzar <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </CardContent>
          </Card>

          {/* FASE B */}
          {(minuta.fase_a_completada || minuta.fase_actual === 'fase_b') && (
            <Card className={minuta.fase_actual === 'fase_b' ? 'ring-2 ring-blue-400' : ''}>
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  📤 Fase B - Tramitación
                  {minuta.fase_b_completada && <Badge className="bg-green-500 text-white">Completada</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {minuta.fase_actual === 'fase_b' && !minuta.fase_b_completada ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Fecha presentación</Label>
                        <Input 
                          type="date"
                          value={formFaseB.fecha_presentacion}
                          onChange={(e) => setFormFaseB({...formFaseB, fecha_presentacion: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Importe total (€)</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={formFaseB.importe_total_minuta}
                          onChange={(e) => setFormFaseB({...formFaseB, importe_total_minuta: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={formFaseB.propuesta_intereses_fase_b}
                        onCheckedChange={(c) => setFormFaseB({...formFaseB, propuesta_intereses_fase_b: c})}
                      />
                      <Label>Propuesta de intereses realizada</Label>
                    </div>
                    <div>
                      <Label>Prioridad</Label>
                      <Select value={formFaseB.prioridad} onValueChange={(v) => setFormFaseB({...formFaseB, prioridad: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PRIORIDADES).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Observaciones</Label>
                      <Textarea 
                        value={formFaseB.observaciones_fase_b}
                        onChange={(e) => setFormFaseB({...formFaseB, observaciones_fase_b: e.target.value})}
                        rows={2}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={handleGuardarFaseB} disabled={saving}>
                        Guardar cambios
                      </Button>
                      <Button 
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                        onClick={() => onAvanzarFase(minuta, 'fase_b')}
                      >
                        Completar Fase B y Avanzar <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-500">Fecha presentación</Label>
                      <p>{formatDate(minuta.fecha_presentacion)}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Importe</Label>
                      <p className="font-medium text-green-600">
                        {minuta.importe_total_minuta ? `€${minuta.importe_total_minuta.toLocaleString('es-ES', { minimumFractionDigits: 2 })}` : '-'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Propuesta intereses</Label>
                      <p>{minuta.propuesta_intereses_fase_b ? 'Sí' : 'No'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Prioridad</Label>
                      <Badge className={PRIORIDADES[minuta.prioridad]?.color}>{PRIORIDADES[minuta.prioridad]?.nombre}</Badge>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* FASE C */}
          {(minuta.fase_b_completada || minuta.fase_actual === 'fase_c') && (
            <Card className={minuta.fase_actual === 'fase_c' && !minuta.fase_c_completada ? 'ring-2 ring-green-400' : ''}>
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  ✅ Fase C - Finalización
                  {minuta.fase_c_completada && <Badge className="bg-green-500 text-white">Nº {minuta.numero_minuta}</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {minuta.fase_actual === 'fase_c' && !minuta.fase_c_completada ? (
                  <>
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                      <p className="text-green-800">Al completar esta fase se asignará automáticamente un número de minuta.</p>
                    </div>
                    <div>
                      <Label>Importe final (€)</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={formFaseC.importe_minuta_final}
                        onChange={(e) => setFormFaseC({...formFaseC, importe_minuta_final: e.target.value})}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={formFaseC.propuesta_intereses_fase_c}
                        onCheckedChange={(c) => setFormFaseC({...formFaseC, propuesta_intereses_fase_c: c})}
                      />
                      <Label>Consta propuesta de intereses</Label>
                    </div>
                    <div>
                      <Label>Observaciones</Label>
                      <Textarea 
                        value={formFaseC.observaciones_fase_c}
                        onChange={(e) => setFormFaseC({...formFaseC, observaciones_fase_c: e.target.value})}
                        rows={2}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={handleGuardarFaseC} disabled={saving}>
                        Guardar cambios
                      </Button>
                      <Button 
                        className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                        onClick={() => onAvanzarFase(minuta, 'fase_c')}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Finalizar y Asignar Número
                      </Button>
                    </div>
                  </>
                ) : minuta.fase_c_completada ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-500">Número de minuta</Label>
                      <p className="text-xl font-bold text-green-600">{minuta.numero_minuta}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Importe final</Label>
                      <p className="font-medium">
                        {minuta.importe_minuta_final ? `€${minuta.importe_minuta_final.toLocaleString('es-ES', { minimumFractionDigits: 2 })}` : '-'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Realizado por</Label>
                      <p>{minuta.realizado_por_nombre || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Fecha finalización</Label>
                      <p>{formatDateTime(minuta.fecha_completada_fase_c)}</p>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// COMPONENTE: TAREA COSTAS (Similar a Abogados)
// ============================================================================
const TareaCostasTab = ({ tareas, loading, search, setSearch, filterCompletada, setFilterCompletada, filterPrioridad, setFilterPrioridad, loadTareas, handleDelete, handleToggleCompletada, usuarios, config }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [form, setForm] = useState({
    descripcion: '',
    observaciones: '',
    fecha_fin: '',
    asignado_a_id: '',
    asignado_a_nombre: '',
    prioridad: 'media'
  });

  const resetForm = () => {
    setForm({
      descripcion: '',
      observaciones: '',
      fecha_fin: '',
      asignado_a_id: '',
      asignado_a_nombre: '',
      prioridad: 'media'
    });
    setSelectedExpediente(null);
    setEditingTarea(null);
  };

  const handleOpenNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (tarea) => {
    setForm({
      descripcion: tarea.descripcion || '',
      observaciones: tarea.observaciones || '',
      fecha_fin: tarea.fecha_fin || '',
      asignado_a_id: tarea.asignado_a_id?.toString() || '',
      asignado_a_nombre: tarea.asignado_a_nombre || '',
      prioridad: tarea.prioridad || 'media'
    });
    setSelectedExpediente(tarea.expediente_id ? {
      id: tarea.expediente_id,
      numero_expediente: tarea.expediente_numero
    } : null);
    setEditingTarea(tarea);
    setIsDialogOpen(true);
  };

  const handleUsuarioChange = (userId) => {
    const user = usuarios.find(u => u.id.toString() === userId);
    setForm({
      ...form,
      asignado_a_id: userId,
      asignado_a_nombre: user?.nombre || user?.email || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.descripcion) {
      toast({ title: 'Error', description: 'La descripción es obligatoria', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        expediente_id: selectedExpediente?.id || null,
        expediente_numero: selectedExpediente?.numero_expediente || null,
        asignado_a_id: form.asignado_a_id ? parseInt(form.asignado_a_id) : null,
        fecha_fin: form.fecha_fin || null
      };
      
      if (editingTarea) {
        await tareasDespachoAPI.costas.update(editingTarea.id, data);
        toast({ title: 'Tarea actualizada' });
      } else {
        await tareasDespachoAPI.costas.create(data);
        toast({ title: 'Tarea creada' });
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const Icon = config.icon;

  return (
    <>
      {/* Header */}
      <Card className={`${config.lightBg} border-l-4 ${config.borderColor}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{config.nombre}</h2>
              <p className="text-sm text-gray-600">Costas procesales pendientes de pago</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar tareas..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadTareas()}
                />
              </div>
            </div>
            
            <Select value={filterCompletada} onValueChange={setFilterCompletada}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="pendientes">Pendientes</SelectItem>
                <SelectItem value="completadas">Completadas</SelectItem>
              </SelectContent>
            </Select>
            
            <Button className={`${config.bgColor} hover:opacity-90 text-white`} onClick={handleOpenNew}>
              <Plus className="w-4 h-4 mr-2" />
              NUEVA TAREA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : tareas.length > 0 ? (
            <div className="divide-y">
              {tareas.map((tarea) => (
                <div key={tarea.id} className={`p-4 hover:bg-gray-50 ${tarea.completada ? 'bg-gray-50 opacity-75' : ''}`}>
                  <div className="flex items-start gap-4">
                    <button 
                      onClick={() => handleToggleCompletada(tarea)}
                      className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        tarea.completada ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-500'
                      }`}
                    >
                      {tarea.completada && <Check className="w-4 h-4" />}
                    </button>
                    
                    <div className="flex-1">
                      <p className={`font-medium ${tarea.completada ? 'line-through text-gray-500' : ''}`}>
                        {tarea.descripcion}
                      </p>
                      {tarea.observaciones && (
                        <p className="text-sm text-gray-600 mt-1">{tarea.observaciones}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                        <Badge className={PRIORIDADES[tarea.prioridad]?.color}>
                          {PRIORIDADES[tarea.prioridad]?.nombre}
                        </Badge>
                        {tarea.expediente_numero && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <FileText className="w-3 h-3" />{tarea.expediente_numero}
                          </span>
                        )}
                        {tarea.fecha_fin && (
                          <span className="flex items-center gap-1 text-gray-600">
                            <Calendar className="w-3 h-3" />Fin: {formatDate(tarea.fecha_fin)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-1">
                      <button className="p-1 hover:bg-gray-200 rounded" onClick={() => handleEdit(tarea)}>
                        <Edit className="w-4 h-4 text-gray-600" />
                      </button>
                      <button className="p-1 hover:bg-gray-200 rounded" onClick={() => handleDelete(tarea)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay tareas</p>
              <Button variant="outline" className="mt-4" onClick={handleOpenNew}>
                <Plus className="w-4 h-4 mr-2" />Crear primera tarea
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              {editingTarea ? 'Editar Tarea' : 'Nueva Tarea Costas a Pagar'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label>Expediente relacionado</Label>
                <ExpedienteSearch
                  value={selectedExpediente}
                  onChange={setSelectedExpediente}
                  placeholder="Buscar expediente por número o título..."
                />
              </div>
              
              <div>
                <Label>Descripción *</Label>
                <Textarea 
                  value={form.descripcion}
                  onChange={(e) => setForm({...form, descripcion: e.target.value})}
                  placeholder="Descripción de la tarea..."
                  rows={2}
                  required
                />
              </div>
              
              <div>
                <Label>Observaciones</Label>
                <Textarea 
                  value={form.observaciones}
                  onChange={(e) => setForm({...form, observaciones: e.target.value})}
                  placeholder="Observaciones adicionales..."
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha fin</Label>
                  <Input 
                    type="date"
                    value={form.fecha_fin}
                    onChange={(e) => setForm({...form, fecha_fin: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Prioridad</Label>
                  <Select value={form.prioridad} onValueChange={(v) => setForm({...form, prioridad: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORIDADES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className={`${config.bgColor} hover:opacity-90 text-white`} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingTarea ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============================================================================
// COMPONENTE: TAREA EJECUCIONES (Con seguimiento)
// ============================================================================
const TareaEjecucionesTab = ({ tareas, loading, search, setSearch, filterCompletada, setFilterCompletada, filterPrioridad, setFilterPrioridad, loadTareas, handleDelete, handleToggleCompletada, usuarios, config }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [selectedTarea, setSelectedTarea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [form, setForm] = useState({
    costas_reclamadas: '',
    importe: '',
    fecha_resolucion_judicial: '',
    fecha_ejecucion: '',
    asignado_a_id: '',
    asignado_a_nombre: '',
    prioridad: 'media'
  });

  const resetForm = () => {
    setForm({
      costas_reclamadas: '',
      importe: '',
      fecha_resolucion_judicial: '',
      fecha_ejecucion: '',
      asignado_a_id: '',
      asignado_a_nombre: '',
      prioridad: 'media'
    });
    setSelectedExpediente(null);
    setEditingTarea(null);
  };

  const handleOpenNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleUsuarioChange = (userId) => {
    const user = usuarios.find(u => u.id.toString() === userId);
    setForm({
      ...form,
      asignado_a_id: userId,
      asignado_a_nombre: user?.nombre || user?.email || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setSaving(true);
    try {
      const data = {
        ...form,
        expediente_id: selectedExpediente?.id || null,
        expediente_numero: selectedExpediente?.numero_expediente || null,
        asignado_a_id: form.asignado_a_id ? parseInt(form.asignado_a_id) : null,
        importe: form.importe ? parseFloat(form.importe) : null,
        fecha_resolucion_judicial: form.fecha_resolucion_judicial || null,
        fecha_ejecucion: form.fecha_ejecucion || null
      };
      
      if (editingTarea) {
        await tareasDespachoAPI.ejecuciones.update(editingTarea.id, data);
        toast({ title: 'Tarea actualizada' });
      } else {
        await tareasDespachoAPI.ejecuciones.create(data);
        toast({ title: 'Tarea creada' });
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetail = (tarea) => {
    setSelectedTarea(tarea);
    setIsDetailOpen(true);
  };

  const Icon = config.icon;

  return (
    <>
      {/* Header */}
      <Card className={`${config.lightBg} border-l-4 ${config.borderColor}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{config.nombre}</h2>
              <p className="text-sm text-gray-600">Ejecuciones judiciales con seguimiento</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar ejecuciones..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadTareas()}
                />
              </div>
            </div>
            
            <Select value={filterCompletada} onValueChange={setFilterCompletada}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="pendientes">Pendientes</SelectItem>
                <SelectItem value="completadas">Completadas</SelectItem>
              </SelectContent>
            </Select>
            
            <Button className={`${config.bgColor} hover:opacity-90 text-white`} onClick={handleOpenNew}>
              <Plus className="w-4 h-4 mr-2" />
              NUEVA EJECUCIÓN
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : tareas.length > 0 ? (
            <div className="divide-y">
              {tareas.map((tarea) => (
                <div 
                  key={tarea.id} 
                  className={`p-4 hover:bg-gray-50 cursor-pointer ${tarea.completada ? 'bg-gray-50 opacity-75' : ''}`}
                  onClick={() => handleViewDetail(tarea)}
                >
                  <div className="flex items-start gap-4">
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleToggleCompletada(tarea); }}
                      className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        tarea.completada ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-500'
                      }`}
                    >
                      {tarea.completada && <Check className="w-4 h-4" />}
                    </button>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {tarea.expediente_numero && (
                          <span className="font-medium text-blue-600">{tarea.expediente_numero}</span>
                        )}
                        <Badge className={PRIORIDADES[tarea.prioridad]?.color}>
                          {PRIORIDADES[tarea.prioridad]?.nombre}
                        </Badge>
                        {tarea.borrador_demanda_hecho && (
                          <Badge className="bg-green-100 text-green-700">Borrador hecho</Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-700">
                        {tarea.costas_reclamadas || 'Sin descripción de costas'}
                      </p>
                      
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-600">
                        {tarea.importe && (
                          <span className="font-medium text-green-600">
                            €{tarea.importe.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                          </span>
                        )}
                        {tarea.fecha_ejecucion && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />Ejecución: {formatDate(tarea.fecha_ejecucion)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewDetail(tarea); }}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(tarea); }}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay ejecuciones</p>
              <Button variant="outline" className="mt-4" onClick={handleOpenNew}>
                <Plus className="w-4 h-4 mr-2" />Crear primera ejecución
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nueva */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              Nueva Tarea Ejecución
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label>Expediente relacionado</Label>
                <ExpedienteSearch
                  value={selectedExpediente}
                  onChange={setSelectedExpediente}
                  placeholder="Buscar expediente por número o título..."
                />
              </div>
              
              <div>
                <Label>Costas reclamadas</Label>
                <Textarea 
                  value={form.costas_reclamadas}
                  onChange={(e) => setForm({...form, costas_reclamadas: e.target.value})}
                  placeholder="Descripción de las costas que se reclaman..."
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Importe (€)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={form.importe}
                    onChange={(e) => setForm({...form, importe: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Prioridad</Label>
                  <Select value={form.prioridad} onValueChange={(v) => setForm({...form, prioridad: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORIDADES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha resolución judicial</Label>
                  <Input 
                    type="date"
                    value={form.fecha_resolucion_judicial}
                    onChange={(e) => setForm({...form, fecha_resolucion_judicial: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Fecha ejecución</Label>
                  <Input 
                    type="date"
                    value={form.fecha_ejecucion}
                    onChange={(e) => setForm({...form, fecha_ejecucion: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Asignar a</Label>
                <Select value={form.asignado_a_id} onValueChange={handleUsuarioChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar responsable" />
                  </SelectTrigger>
                  <SelectContent>
                    {usuarios.map(u => (
                      <SelectItem key={u.id} value={u.id.toString()}>
                        {u.nombre || u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className={`${config.bgColor} hover:opacity-90 text-white`} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Crear Ejecución
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle con Seguimiento */}
      <EjecucionDetailDialog 
        tarea={selectedTarea}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        loadTareas={loadTareas}
      />
    </>
  );
};

// ============================================================================
// DIALOG: Detalle de Ejecución con seguimiento
// ============================================================================
const EjecucionDetailDialog = ({ tarea, isOpen, onClose, loadTareas }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (tarea) {
      setForm({
        cliente_cobrado: tarea.cliente_cobrado || false,
        cliente_cobrado_fecha: tarea.cliente_cobrado_fecha || '',
        cliente_cobrado_notas: tarea.cliente_cobrado_notas || '',
        dinero_consignado: tarea.dinero_consignado || false,
        dinero_consignado_fecha: tarea.dinero_consignado_fecha || '',
        dinero_consignado_notas: tarea.dinero_consignado_notas || '',
        borrador_demanda_hecho: tarea.borrador_demanda_hecho || false,
        fecha_presentacion_demanda: tarea.fecha_presentacion_demanda || '',
        observaciones: tarea.observaciones || ''
      });
    }
  }, [tarea]);

  const handleGuardar = async () => {
    setSaving(true);
    try {
      await tareasDespachoAPI.ejecuciones.update(tarea.id, form);
      toast({ title: 'Seguimiento actualizado' });
      loadTareas();
      onClose();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!tarea) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5" />
            Seguimiento de Ejecución
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info básica */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Expediente</Label>
                  <p className="font-medium">{tarea.expediente_numero || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Importe</Label>
                  <p className="font-medium text-green-600">
                    {tarea.importe ? `€${tarea.importe.toLocaleString('es-ES', { minimumFractionDigits: 2 })}` : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Fecha resolución judicial</Label>
                  <p>{formatDate(tarea.fecha_resolucion_judicial)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Fecha ejecución</Label>
                  <p>{formatDate(tarea.fecha_ejecucion)}</p>
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-gray-500">Costas reclamadas</Label>
                <p>{tarea.costas_reclamadas || '-'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Seguimiento */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base">Seguimiento del Expediente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Cliente cobrado */}
              <div className="p-3 border rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={form.cliente_cobrado}
                    onCheckedChange={(c) => setForm({...form, cliente_cobrado: c})}
                  />
                  <Label className="font-medium">¿Se preguntó al cliente si ha cobrado?</Label>
                </div>
                {form.cliente_cobrado && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <Label className="text-sm">Fecha consulta</Label>
                      <Input 
                        type="date"
                        value={form.cliente_cobrado_fecha}
                        onChange={(e) => setForm({...form, cliente_cobrado_fecha: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Notas</Label>
                      <Input 
                        value={form.cliente_cobrado_notas}
                        onChange={(e) => setForm({...form, cliente_cobrado_notas: e.target.value})}
                        placeholder="Respuesta del cliente..."
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Dinero consignado */}
              <div className="p-3 border rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={form.dinero_consignado}
                    onCheckedChange={(c) => setForm({...form, dinero_consignado: c})}
                  />
                  <Label className="font-medium">¿Se preguntó al procurador si hay dinero consignado?</Label>
                </div>
                {form.dinero_consignado && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <Label className="text-sm">Fecha consulta</Label>
                      <Input 
                        type="date"
                        value={form.dinero_consignado_fecha}
                        onChange={(e) => setForm({...form, dinero_consignado_fecha: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Notas</Label>
                      <Input 
                        value={form.dinero_consignado_notas}
                        onChange={(e) => setForm({...form, dinero_consignado_notas: e.target.value})}
                        placeholder="Respuesta del procurador..."
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Borrador demanda */}
              <div className="p-3 border rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    checked={form.borrador_demanda_hecho}
                    onCheckedChange={(c) => setForm({...form, borrador_demanda_hecho: c})}
                  />
                  <Label className="font-medium">¿Se ha hecho borrador de demanda?</Label>
                </div>
                {form.borrador_demanda_hecho && (
                  <div className="mt-2">
                    <Label className="text-sm">Fecha de presentación</Label>
                    <Input 
                      type="date"
                      value={form.fecha_presentacion_demanda}
                      onChange={(e) => setForm({...form, fecha_presentacion_demanda: e.target.value})}
                    />
                  </div>
                )}
              </div>

              {/* Observaciones */}
              <div>
                <Label>Observaciones generales</Label>
                <Textarea 
                  value={form.observaciones}
                  onChange={(e) => setForm({...form, observaciones: e.target.value})}
                  rows={3}
                  placeholder="Notas adicionales sobre el seguimiento..."
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleGuardar} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Guardar Seguimiento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// COMPONENTE: TAREA PASTELITOS
// ============================================================================
const TareaPastelitosTab = ({ tareas, loading, search, setSearch, filterCompletada, setFilterCompletada, filterPrioridad, setFilterPrioridad, loadTareas, handleDelete, handleToggleCompletada, usuarios, config }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTarea, setEditingTarea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedExpediente, setSelectedExpediente] = useState(null);
  const [form, setForm] = useState({
    tarea_realizar: '',
    comentarios: '',
    realizado_por_id: '',
    realizado_por_nombre: '',
    prioridad: 'media'
  });

  const resetForm = () => {
    setForm({
      tarea_realizar: '',
      comentarios: '',
      realizado_por_id: '',
      realizado_por_nombre: '',
      prioridad: 'media'
    });
    setSelectedExpediente(null);
    setEditingTarea(null);
  };

  const handleOpenNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (tarea) => {
    setForm({
      tarea_realizar: tarea.tarea_realizar || '',
      comentarios: tarea.comentarios || '',
      realizado_por_id: tarea.realizado_por_id?.toString() || '',
      realizado_por_nombre: tarea.realizado_por_nombre || '',
      prioridad: tarea.prioridad || 'media'
    });
    setSelectedExpediente(tarea.expediente_id ? {
      id: tarea.expediente_id,
      numero_expediente: tarea.expediente_numero
    } : null);
    setEditingTarea(tarea);
    setIsDialogOpen(true);
  };

  const handleUsuarioChange = (userId) => {
    const user = usuarios.find(u => u.id.toString() === userId);
    setForm({
      ...form,
      realizado_por_id: userId,
      realizado_por_nombre: user?.nombre || user?.email || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.tarea_realizar) {
      toast({ title: 'Error', description: 'La tarea a realizar es obligatoria', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        expediente_id: selectedExpediente?.id || null,
        expediente_numero: selectedExpediente?.numero_expediente || null,
        realizado_por_id: form.realizado_por_id ? parseInt(form.realizado_por_id) : null
      };
      
      if (editingTarea) {
        await tareasDespachoAPI.pastelitos.update(editingTarea.id, data);
        toast({ title: 'Tarea actualizada' });
      } else {
        await tareasDespachoAPI.pastelitos.create(data);
        toast({ title: 'Tarea creada' });
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadTareas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const Icon = config.icon;

  return (
    <>
      {/* Header */}
      <Card className={`${config.lightBg} border-l-4 ${config.borderColor}`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{config.nombre}</h2>
              <p className="text-sm text-gray-600">Tareas especiales del despacho</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar tareas..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadTareas()}
                />
              </div>
            </div>
            
            <Select value={filterCompletada} onValueChange={setFilterCompletada}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="pendientes">Pendientes</SelectItem>
                <SelectItem value="completadas">Completadas</SelectItem>
              </SelectContent>
            </Select>
            
            <Button className={`${config.bgColor} hover:opacity-90 text-white`} onClick={handleOpenNew}>
              <Plus className="w-4 h-4 mr-2" />
              NUEVA TAREA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : tareas.length > 0 ? (
            <div className="divide-y">
              {tareas.map((tarea) => (
                <div key={tarea.id} className={`p-4 hover:bg-gray-50 ${tarea.completada ? 'bg-gray-50 opacity-75' : ''}`}>
                  <div className="flex items-start gap-4">
                    <button 
                      onClick={() => handleToggleCompletada(tarea)}
                      className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        tarea.completada ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-500'
                      }`}
                    >
                      {tarea.completada && <Check className="w-4 h-4" />}
                    </button>
                    
                    <div className="flex-1">
                      <p className={`font-medium ${tarea.completada ? 'line-through text-gray-500' : ''}`}>
                        {tarea.tarea_realizar}
                      </p>
                      {tarea.comentarios && (
                        <p className="text-sm text-gray-600 mt-1">{tarea.comentarios}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                        <Badge className={PRIORIDADES[tarea.prioridad]?.color}>
                          {PRIORIDADES[tarea.prioridad]?.nombre}
                        </Badge>
                        {tarea.realizado_por_nombre && (
                          <span className="flex items-center gap-1 text-gray-600">
                            <User className="w-3 h-3" />Realiza: {tarea.realizado_por_nombre}
                          </span>
                        )}
                        {tarea.expediente_numero && (
                          <span className="flex items-center gap-1 text-blue-600">
                            <FileText className="w-3 h-3" />{tarea.expediente_numero}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-1">
                      <button className="p-1 hover:bg-gray-200 rounded" onClick={() => handleEdit(tarea)}>
                        <Edit className="w-4 h-4 text-gray-600" />
                      </button>
                      <button className="p-1 hover:bg-gray-200 rounded" onClick={() => handleDelete(tarea)}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay tareas</p>
              <Button variant="outline" className="mt-4" onClick={handleOpenNew}>
                <Plus className="w-4 h-4 mr-2" />Crear primera tarea
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              {editingTarea ? 'Editar Tarea' : 'Nueva Tarea Pastelitos'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <Label>Expediente relacionado</Label>
                <ExpedienteSearch
                  value={selectedExpediente}
                  onChange={setSelectedExpediente}
                  placeholder="Buscar expediente por número o título..."
                />
              </div>
              
              <div>
                <Label>Tarea a realizar *</Label>
                <Textarea 
                  value={form.tarea_realizar}
                  onChange={(e) => setForm({...form, tarea_realizar: e.target.value})}
                  placeholder="Describe la tarea..."
                  rows={2}
                  required
                />
              </div>
              
              <div>
                <Label>Comentarios</Label>
                <Textarea 
                  value={form.comentarios}
                  onChange={(e) => setForm({...form, comentarios: e.target.value})}
                  placeholder="Comentarios adicionales..."
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Quien realiza la tarea</Label>
                  <Select value={form.realizado_por_id} onValueChange={handleUsuarioChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {usuarios.map(u => (
                        <SelectItem key={u.id} value={u.id.toString()}>
                          {u.nombre || u.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Prioridad</Label>
                  <Select value={form.prioridad} onValueChange={(v) => setForm({...form, prioridad: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORIDADES).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className={`${config.bgColor} hover:opacity-90 text-white`} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingTarea ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============================================================================
// COMPONENTE: ATENCIÓN CLIENTE
// ============================================================================
const ORIGENES = {
  telefono: { nombre: 'Teléfono', icon: Phone, color: 'bg-green-100 text-green-700' },
  email: { nombre: 'Email', icon: Mail, color: 'bg-blue-100 text-blue-700' },
  whatsapp: { nombre: 'WhatsApp', icon: MessageSquare, color: 'bg-emerald-100 text-emerald-700' },
  web: { nombre: 'Web', icon: Globe, color: 'bg-purple-100 text-purple-700' },
  instagram: { nombre: 'Instagram', icon: Instagram, color: 'bg-pink-100 text-pink-700' }
};

const ESTADOS_CONSULTA = {
  pendiente: { nombre: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  en_proceso: { nombre: 'En Proceso', color: 'bg-blue-100 text-blue-700' },
  respondido: { nombre: 'Respondido', color: 'bg-green-100 text-green-700' },
  completado: { nombre: 'Completado', color: 'bg-gray-100 text-gray-700' },
  archivado: { nombre: 'Archivado', color: 'bg-gray-200 text-gray-500' }
};

const AtencionClienteTab = ({ usuarios, config }) => {
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('pendiente');
  const [filterOrigen, setFilterOrigen] = useState('');
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [editingConsulta, setEditingConsulta] = useState(null);
  const [selectedConsulta, setSelectedConsulta] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [plantillas, setPlantillas] = useState([]);
  const [expedientesSearch, setExpedientesSearch] = useState([]);
  const [searchingExpediente, setSearchingExpediente] = useState(false);
  
  const [form, setForm] = useState({
    origen: 'telefono',
    cliente_nombre: '',
    cliente_apellidos: '',
    cliente_email: '',
    cliente_telefono: '',
    expediente_id: null,
    expediente_numero: '',
    asunto: '',
    descripcion_consulta: '',
    prioridad: 'media',
    asignado_a_id: '',
    asignado_a_nombre: ''
  });
  const [adjuntosNuevaConsulta, setAdjuntosNuevaConsulta] = useState([]);
  const [uploadingAdjunto, setUploadingAdjunto] = useState(false);

  useEffect(() => {
    loadConsultas();
    loadPlantillas();
  }, []);

  useEffect(() => {
    loadConsultas();
  }, [filterEstado, filterOrigen]);

  const loadConsultas = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterEstado) params.estado = filterEstado;
      if (filterOrigen) params.origen = filterOrigen;
      if (search) params.search = search;
      
      const res = await atencionClienteAPI.getConsultas(params);
      setConsultas(res.data.consultas);
    } catch (error) {
      console.error('Error cargando consultas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlantillas = async () => {
    try {
      const res = await atencionClienteAPI.getPlantillas(true);
      setPlantillas(res.data);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
    }
  };

  const resetForm = () => {
    setForm({
      origen: 'telefono',
      cliente_nombre: '',
      cliente_apellidos: '',
      cliente_email: '',
      cliente_telefono: '',
      expediente_id: null,
      expediente_numero: '',
      asunto: '',
      descripcion_consulta: '',
      prioridad: 'media',
      asignado_a_id: '',
      asignado_a_nombre: ''
    });
    setEditingConsulta(null);
    setExpedientesSearch([]);
    setAdjuntosNuevaConsulta([]);
  };

  const handleOpenNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (consulta) => {
    setForm({
      origen: consulta.origen,
      cliente_nombre: consulta.cliente_nombre || '',
      cliente_apellidos: consulta.cliente_apellidos || '',
      cliente_email: consulta.cliente_email || '',
      cliente_telefono: consulta.cliente_telefono || '',
      expediente_id: consulta.expediente_id,
      expediente_numero: consulta.expediente_numero || '',
      asunto: consulta.asunto || '',
      descripcion_consulta: consulta.descripcion_consulta || '',
      prioridad: consulta.prioridad || 'media',
      asignado_a_id: consulta.asignado_a_id || '',
      asignado_a_nombre: consulta.asignado_a_nombre || ''
    });
    setEditingConsulta(consulta);
    setIsDialogOpen(true);
  };

  const handleDelete = async (consulta) => {
    if (!window.confirm('¿Está seguro de eliminar esta consulta?')) return;
    try {
      await atencionClienteAPI.deleteConsulta(consulta.id);
      toast({ title: 'Consulta eliminada' });
      loadConsultas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente_nombre || !form.asunto || !form.descripcion_consulta) {
      toast({ title: 'Error', description: 'Complete los campos obligatorios', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        asignado_a_id: form.asignado_a_id ? parseInt(form.asignado_a_id) : null
      };
      
      let consultaId;
      if (editingConsulta) {
        await atencionClienteAPI.updateConsulta(editingConsulta.id, data);
        consultaId = editingConsulta.id;
        toast({ title: 'Consulta actualizada' });
      } else {
        const res = await atencionClienteAPI.createConsulta(data);
        consultaId = res.data.id;
        toast({ title: 'Consulta creada' });
      }
      
      // Subir adjuntos si hay
      if (adjuntosNuevaConsulta.length > 0 && consultaId) {
        for (const file of adjuntosNuevaConsulta) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await atencionClienteAPI.subirAdjuntoConsulta(consultaId, formData);
          } catch (err) {
            console.error('Error subiendo adjunto:', err);
          }
        }
        toast({ title: `${adjuntosNuevaConsulta.length} archivo(s) adjuntado(s)` });
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadConsultas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDetail = async (consulta) => {
    try {
      const res = await atencionClienteAPI.getConsulta(consulta.id);
      setSelectedConsulta(res.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast({ title: 'Error al cargar detalles', variant: 'destructive' });
    }
  };

  const searchExpedientes = async (query) => {
    if (query.length < 2) {
      setExpedientesSearch([]);
      return;
    }
    setSearchingExpediente(true);
    try {
      const res = await atencionClienteAPI.buscarExpedientes(query);
      setExpedientesSearch(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setSearchingExpediente(false);
    }
  };

  const Icon = config.icon;

  return (
    <>
      {/* Header */}
      <Card className={`${config.lightBg} border-l-4 ${config.borderColor}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{config.nombre}</h2>
                <p className="text-sm text-gray-600">Peticiones de información de clientes por teléfono, email o WhatsApp</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por cliente, asunto..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadConsultas()}
                />
              </div>
            </div>
            
            <Select value={filterEstado || 'all'} onValueChange={(v) => setFilterEstado(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(ESTADOS_CONSULTA).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterOrigen || 'all'} onValueChange={(v) => setFilterOrigen(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Origen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(ORIGENES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button className={`${config.bgColor} hover:opacity-90 text-white`} onClick={handleOpenNew}>
              <Plus className="w-4 h-4 mr-2" />
              NUEVA CONSULTA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de consultas */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : consultas.length > 0 ? (
            <div className="divide-y">
              {consultas.map((consulta) => {
                const origenConfig = ORIGENES[consulta.origen] || ORIGENES.telefono;
                const estadoConfig = ESTADOS_CONSULTA[consulta.estado] || ESTADOS_CONSULTA.pendiente;
                const OrigenIcon = origenConfig.icon;
                
                return (
                  <div 
                    key={consulta.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleOpenDetail(consulta)}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${origenConfig.color}`}>
                        <OrigenIcon className="w-5 h-5" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-medium text-gray-900">
                              {consulta.cliente_nombre} {consulta.cliente_apellidos}
                            </p>
                            <p className="text-sm text-gray-600 truncate">{consulta.asunto}</p>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button 
                              className="p-1 hover:bg-gray-200 rounded"
                              onClick={() => handleEdit(consulta)}
                              title="Editar"
                            >
                              <Edit className="w-4 h-4 text-gray-600" />
                            </button>
                            <button 
                              className="p-1 hover:bg-gray-200 rounded"
                              onClick={() => handleDelete(consulta)}
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                          <Badge className={estadoConfig.color}>
                            {estadoConfig.nombre}
                          </Badge>
                          <Badge className={PRIORIDADES[consulta.prioridad]?.color || ''}>
                            {PRIORIDADES[consulta.prioridad]?.nombre || consulta.prioridad}
                          </Badge>
                          
                          {consulta.expediente_numero && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <FileText className="w-3 h-3" />
                              {consulta.expediente_numero}
                            </span>
                          )}
                          
                          {consulta.cliente_telefono && (
                            <span className="flex items-center gap-1 text-gray-600">
                              <Phone className="w-3 h-3" />
                              {consulta.cliente_telefono}
                            </span>
                          )}
                          
                          {consulta.cliente_email && (
                            <span className="flex items-center gap-1 text-gray-600">
                              <Mail className="w-3 h-3" />
                              {consulta.cliente_email}
                            </span>
                          )}
                          
                          <span className="flex items-center gap-1 text-gray-500">
                            <Calendar className="w-3 h-3" />
                            {formatDateTime(consulta.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay consultas de atención cliente</p>
              <Button variant="outline" className="mt-4" onClick={handleOpenNew}>
                <Plus className="w-4 h-4 mr-2" />
                Crear primera consulta
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nueva/Editar Consulta */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              {editingConsulta ? 'Editar Consulta' : 'Nueva Consulta de Cliente'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Origen */}
            <div>
              <Label>Origen de la consulta *</Label>
              <div className="flex gap-2 mt-2">
                {Object.entries(ORIGENES).map(([key, val]) => {
                  const OrigenIcon = val.icon;
                  return (
                    <Button
                      key={key}
                      type="button"
                      variant={form.origen === key ? 'default' : 'outline'}
                      className={form.origen === key ? config.bgColor : ''}
                      onClick={() => setForm({...form, origen: key})}
                    >
                      <OrigenIcon className="w-4 h-4 mr-2" />
                      {val.nombre}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Datos del cliente */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={form.cliente_nombre}
                  onChange={(e) => setForm({...form, cliente_nombre: e.target.value})}
                  placeholder="Nombre del cliente"
                  required
                />
              </div>
              <div>
                <Label>Apellidos</Label>
                <Input
                  value={form.cliente_apellidos}
                  onChange={(e) => setForm({...form, cliente_apellidos: e.target.value})}
                  placeholder="Apellidos"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={form.cliente_telefono}
                  onChange={(e) => setForm({...form, cliente_telefono: e.target.value})}
                  placeholder="600 123 456"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.cliente_email}
                  onChange={(e) => setForm({...form, cliente_email: e.target.value})}
                  placeholder="cliente@email.com"
                />
              </div>
            </div>

            {/* Expediente relacionado */}
            <div>
              <Label>Expediente relacionado (opcional)</Label>
              <div className="relative">
                <Input
                  value={form.expediente_numero}
                  onChange={(e) => {
                    setForm({...form, expediente_numero: e.target.value, expediente_id: null});
                    searchExpedientes(e.target.value);
                  }}
                  placeholder="Buscar expediente por número o cliente..."
                />
                {searchingExpediente && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />
                )}
              </div>
              {expedientesSearch.length > 0 && (
                <div className="mt-1 border rounded-md shadow-sm max-h-40 overflow-y-auto">
                  {expedientesSearch.map((exp) => (
                    <div
                      key={exp.id}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setForm({
                          ...form,
                          expediente_id: exp.id,
                          expediente_numero: exp.numero_expediente
                        });
                        setExpedientesSearch([]);
                      }}
                    >
                      <p className="font-medium">{exp.numero_expediente}</p>
                      <p className="text-sm text-gray-600">{exp.cliente_nombre}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Consulta */}
            <div>
              <Label>Asunto *</Label>
              <Input
                value={form.asunto}
                onChange={(e) => setForm({...form, asunto: e.target.value})}
                placeholder="Resumen breve de la consulta"
                required
              />
            </div>

            <div>
              <Label>Descripción de la consulta *</Label>
              <Textarea
                value={form.descripcion_consulta}
                onChange={(e) => setForm({...form, descripcion_consulta: e.target.value})}
                placeholder="Detalle completo de la consulta del cliente..."
                rows={4}
                required
              />
            </div>

            {/* Prioridad y Asignación */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prioridad</Label>
                <Select value={form.prioridad} onValueChange={(v) => setForm({...form, prioridad: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORIDADES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Asignar a</Label>
                <Select 
                  value={form.asignado_a_id?.toString() || 'none'} 
                  onValueChange={(v) => {
                    const user = usuarios.find(u => u.id.toString() === v);
                    setForm({
                      ...form, 
                      asignado_a_id: v && v !== 'none' ? parseInt(v) : null,
                      asignado_a_nombre: user?.nombre || ''
                    });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {usuarios.map((u) => (
                      <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Adjuntos */}
            <div>
              <Label className="flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Archivos adjuntos (opcional)
              </Label>
              <div className="mt-2 space-y-2">
                {adjuntosNuevaConsulta.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {adjuntosNuevaConsulta.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-gray-100 rounded px-2 py-1">
                        <Paperclip className="w-3 h-3 text-gray-500" />
                        <span className="text-sm">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => setAdjuntosNuevaConsulta(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                  <Upload className="w-4 h-4" />
                  Seleccionar archivos
                  <input 
                    type="file" 
                    multiple
                    className="hidden" 
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setAdjuntosNuevaConsulta(prev => [...prev, ...files]);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className={`${config.bgColor} hover:opacity-90 text-white`} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingConsulta ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle Consulta */}
      <ConsultaDetailDialog
        consulta={selectedConsulta}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedConsulta(null);
        }}
        plantillas={plantillas}
        onUpdate={() => {
          loadConsultas();
        }}
        config={config}
      />
    </>
  );
};

// ============================================================================
// COMPONENTE: Dialog Detalle de Consulta con Respuestas
// ============================================================================
const ConsultaDetailDialog = ({ consulta, isOpen, onClose, plantillas, onUpdate, config }) => {
  const [respuestaForm, setRespuestaForm] = useState({
    destinatario_email: '',
    asunto: '',
    cuerpo: '',
    plantilla_id: null,
    adjuntos: []
  });
  const [enviando, setEnviando] = useState(false);
  const [showRespuestaForm, setShowRespuestaForm] = useState(false);
  const [showRespuestaGestionForm, setShowRespuestaGestionForm] = useState(false);
  const [showSeguimientoForm, setShowSeguimientoForm] = useState(false);
  const [showCrearActuacion, setShowCrearActuacion] = useState(false);
  const [showFinalizarConsulta, setShowFinalizarConsulta] = useState(false);
  const [adjuntosDisponibles, setAdjuntosDisponibles] = useState([]);
  const [adjuntosConsulta, setAdjuntosConsulta] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [crearActuacionForm, setCrearActuacionForm] = useState({ titulo: '', observaciones_adicionales: '' });
  const [seguimientoForm, setSeguimientoForm] = useState({ origen: 'telefono', asunto: '', descripcion_consulta: '', prioridad: 'media' });
  const [respuestaGestionForm, setRespuestaGestionForm] = useState({
    tipo_respuesta: 'telefono',
    telefono_contacto: '',
    duracion_llamada: '',
    notas_gestion: ''
  });
  const [finalizarForm, setFinalizarForm] = useState({ crear_actuacion: true, notas_cierre: '' });

  useEffect(() => {
    if (consulta) {
      setRespuestaForm({
        destinatario_email: consulta.cliente_email || '',
        asunto: `Re: ${consulta.asunto || ''}`,
        cuerpo: '',
        plantilla_id: null,
        adjuntos: []
      });
      setRespuestaGestionForm({
        tipo_respuesta: 'telefono',
        telefono_contacto: consulta.cliente_telefono || '',
        duracion_llamada: '',
        notas_gestion: ''
      });
      loadAdjuntos();
      loadAdjuntosConsulta();
    }
  }, [consulta]);

  const loadAdjuntos = async () => {
    if (!consulta) return;
    try {
      const res = await atencionClienteAPI.listarAdjuntos(consulta.id);
      setAdjuntosDisponibles(res.data);
    } catch (error) {
      console.error('Error cargando adjuntos:', error);
    }
  };

  const loadAdjuntosConsulta = async () => {
    if (!consulta) return;
    try {
      const res = await atencionClienteAPI.listarAdjuntosConsulta(consulta.id);
      setAdjuntosConsulta(res.data || []);
    } catch (error) {
      console.error('Error cargando adjuntos de consulta:', error);
      setAdjuntosConsulta([]);
    }
  };

  const handleUploadAdjunto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await atencionClienteAPI.subirAdjunto(consulta.id, formData);
      toast({ title: 'Archivo subido', description: file.name });
      loadAdjuntos();
      // Añadir a la lista de adjuntos del email
      setRespuestaForm(prev => ({
        ...prev,
        adjuntos: [...prev.adjuntos, res.data.path]
      }));
    } catch (error) {
      toast({ title: 'Error al subir archivo', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const toggleAdjunto = (path) => {
    setRespuestaForm(prev => ({
      ...prev,
      adjuntos: prev.adjuntos.includes(path)
        ? prev.adjuntos.filter(p => p !== path)
        : [...prev.adjuntos, path]
    }));
  };

  const handlePlantillaSelect = (plantillaId) => {
    const plantilla = plantillas.find(p => p.id === parseInt(plantillaId));
    if (plantilla) {
      // Reemplazar variables
      let cuerpo = plantilla.cuerpo;
      cuerpo = cuerpo.replace(/\{\{cliente_nombre\}\}/g, `${consulta?.cliente_nombre || ''} ${consulta?.cliente_apellidos || ''}`);
      cuerpo = cuerpo.replace(/\{\{expediente_numero\}\}/g, consulta?.expediente_numero || 'N/A');
      
      setRespuestaForm({
        ...respuestaForm,
        asunto: plantilla.asunto,
        cuerpo: cuerpo,
        plantilla_id: plantilla.id
      });
    }
  };

  const handleEnviarRespuesta = async () => {
    if (!respuestaForm.destinatario_email || !respuestaForm.cuerpo) {
      toast({ title: 'Error', description: 'Complete el email y la respuesta', variant: 'destructive' });
      return;
    }

    setEnviando(true);
    try {
      const res = await atencionClienteAPI.createRespuesta(consulta.id, {
        ...respuestaForm,
        enviar_ahora: true
      });
      
      if (res.data.enviado) {
        const adjCount = res.data.adjuntos_count || 0;
        toast({ 
          title: '¡Email enviado!', 
          description: `La respuesta se ha enviado correctamente${adjCount > 0 ? ` con ${adjCount} adjunto(s)` : ''}` 
        });
        setShowRespuestaForm(false);
        setRespuestaForm({ destinatario_email: consulta.cliente_email || '', asunto: '', cuerpo: '', plantilla_id: null, adjuntos: [] });
        onUpdate();
      } else {
        toast({ 
          title: 'Error al enviar', 
          description: res.data.error || 'No se pudo enviar el email. Verifique la configuración SMTP.',
          variant: 'destructive' 
        });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo enviar la respuesta', variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleCrearSeguimiento = async () => {
    if (!seguimientoForm.asunto || !seguimientoForm.descripcion_consulta) {
      toast({ title: 'Error', description: 'Complete el asunto y descripción', variant: 'destructive' });
      return;
    }
    
    setEnviando(true);
    try {
      const res = await atencionClienteAPI.crearSeguimiento(consulta.id, seguimientoForm);
      toast({ title: 'Seguimiento creado', description: `Seguimiento #${res.data.numero_seguimiento} añadido al hilo` });
      setShowSeguimientoForm(false);
      setSeguimientoForm({ origen: 'telefono', asunto: '', descripcion_consulta: '', prioridad: 'media' });
      onUpdate();
    } catch (error) {
      toast({ title: 'Error al crear seguimiento', variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleCrearActuacion = async () => {
    if (!consulta.expediente_id) {
      toast({ title: 'Error', description: 'La consulta no tiene expediente asociado', variant: 'destructive' });
      return;
    }
    
    setEnviando(true);
    try {
      const res = await atencionClienteAPI.crearActuacion(consulta.id, crearActuacionForm);
      toast({ 
        title: '¡Actuación creada!', 
        description: `Actuación añadida al expediente ${res.data.expediente_numero}` 
      });
      setShowCrearActuacion(false);
      onUpdate();
    } catch (error) {
      const msg = error.response?.data?.detail || 'No se pudo crear la actuación';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleReenviar = async (respuestaId) => {
    try {
      const res = await atencionClienteAPI.reenviarRespuesta(consulta.id, respuestaId);
      if (res.data.enviado) {
        toast({ title: 'Email reenviado correctamente' });
        onUpdate();
      } else {
        toast({ title: 'Error', description: res.data.error, variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error al reenviar', variant: 'destructive' });
    }
  };

  const handleRespuestaGestion = async () => {
    if (!respuestaGestionForm.notas_gestion.trim()) {
      toast({ title: 'Error', description: 'Ingrese las notas de la gestión realizada', variant: 'destructive' });
      return;
    }

    setEnviando(true);
    try {
      await atencionClienteAPI.createRespuesta(consulta.id, {
        tipo_respuesta: respuestaGestionForm.tipo_respuesta,
        cuerpo: respuestaGestionForm.notas_gestion,
        telefono_contacto: respuestaGestionForm.telefono_contacto,
        duracion_llamada: respuestaGestionForm.duracion_llamada,
        enviar_ahora: false
      });
      
      toast({ 
        title: '✅ Gestión registrada', 
        description: `Respuesta por ${respuestaGestionForm.tipo_respuesta === 'telefono' ? 'teléfono' : 'WhatsApp'} guardada correctamente` 
      });
      setShowRespuestaGestionForm(false);
      setRespuestaGestionForm({
        tipo_respuesta: 'telefono',
        telefono_contacto: consulta.cliente_telefono || '',
        duracion_llamada: '',
        notas_gestion: ''
      });
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar la gestión', variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleFinalizarConsulta = async () => {
    setEnviando(true);
    try {
      await atencionClienteAPI.finalizarConsulta(consulta.id, {
        crear_actuacion: finalizarForm.crear_actuacion && consulta.expediente_id,
        notas_cierre: finalizarForm.notas_cierre
      });
      
      toast({ 
        title: '✅ Consulta finalizada', 
        description: finalizarForm.crear_actuacion && consulta.expediente_id 
          ? 'Se ha cerrado la consulta y creado la actuación en el expediente'
          : 'La consulta ha sido cerrada'
      });
      setShowFinalizarConsulta(false);
      onUpdate();
      onClose();
    } catch (error) {
      const msg = error.response?.data?.detail || 'No se pudo finalizar la consulta';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setEnviando(false);
    }
  };

  const handleEliminarAdjuntoConsulta = async (adjuntoPath) => {
    if (!window.confirm('¿Eliminar este archivo adjunto?')) return;
    try {
      await atencionClienteAPI.eliminarAdjuntoConsulta(consulta.id, adjuntoPath);
      toast({ title: 'Archivo eliminado' });
      loadAdjuntosConsulta();
      onUpdate();
    } catch (error) {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  if (!consulta) return null;

  const origenConfig = ORIGENES[consulta.origen] || ORIGENES.telefono;
  const estadoConfig = ESTADOS_CONSULTA[consulta.estado] || ESTADOS_CONSULTA.pendiente;
  const OrigenIcon = origenConfig.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <OrigenIcon className="w-5 h-5" />
            Detalle de Consulta
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info del cliente */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold">
                    {consulta.cliente_nombre} {consulta.cliente_apellidos}
                  </h3>
                  <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
                    {consulta.cliente_telefono && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" /> {consulta.cliente_telefono}
                      </span>
                    )}
                    {consulta.cliente_email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" /> {consulta.cliente_email}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge className={estadoConfig.color}>{estadoConfig.nombre}</Badge>
                  <Badge className={origenConfig.color}>{origenConfig.nombre}</Badge>
                </div>
              </div>
              
              {consulta.expediente_numero && (
                <div className="mt-3 p-2 bg-blue-50 rounded flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">
                    Expediente: {consulta.expediente_numero}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Consulta */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Consulta</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium text-gray-900">{consulta.asunto}</p>
              <p className="mt-2 text-gray-700 whitespace-pre-wrap">{consulta.descripcion_consulta}</p>
              <p className="mt-3 text-xs text-gray-500">
                Recibido: {formatDateTime(consulta.created_at)} • Por: {consulta.creado_por_nombre || 'Sistema'}
              </p>
            </CardContent>
          </Card>

          {/* Adjuntos de la consulta */}
          {adjuntosConsulta.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Archivos Adjuntos ({adjuntosConsulta.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {adjuntosConsulta.map((adj, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
                      <Paperclip className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">{adj.nombre}</span>
                      <div className="flex items-center gap-1 ml-2">
                        <a 
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/atencion-cliente/consultas/${consulta.id}/adjuntos-consulta/${encodeURIComponent(adj.nombre)}/descargar`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 hover:bg-gray-200 rounded"
                          title="Descargar"
                        >
                          <ExternalLink className="w-4 h-4 text-blue-600" />
                        </a>
                        <button
                          onClick={() => handleEliminarAdjuntoConsulta(adj.path)}
                          className="p-1 hover:bg-red-100 rounded"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Respuestas enviadas */}
          {consulta.respuestas && consulta.respuestas.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Gestiones Realizadas ({consulta.respuestas.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {consulta.respuestas.map((resp) => {
                  const tipoResp = resp.tipo_respuesta || 'email';
                  const isEmail = tipoResp === 'email';
                  const isTelefono = tipoResp === 'telefono';
                  const isWhatsapp = tipoResp === 'whatsapp';
                  
                  return (
                    <div key={resp.id} className={`p-3 rounded-lg ${
                      isEmail 
                        ? (resp.enviado ? 'bg-green-50' : 'bg-red-50')
                        : isTelefono 
                          ? 'bg-blue-50' 
                          : 'bg-emerald-50'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {isEmail && <Mail className="w-4 h-4 text-blue-600" />}
                          {isTelefono && <Phone className="w-4 h-4 text-blue-600" />}
                          {isWhatsapp && <MessageSquare className="w-4 h-4 text-emerald-600" />}
                          <div>
                            {isEmail ? (
                              <>
                                <p className="font-medium">{resp.asunto}</p>
                                <p className="text-sm text-gray-600">Para: {resp.destinatario_email}</p>
                              </>
                            ) : (
                              <>
                                <p className="font-medium">
                                  {isTelefono ? 'Llamada telefónica' : 'Mensaje WhatsApp'}
                                </p>
                                {resp.telefono_contacto && (
                                  <p className="text-sm text-gray-600">Tel: {resp.telefono_contacto}</p>
                                )}
                                {resp.duracion_llamada && (
                                  <p className="text-xs text-gray-500">Duración: {resp.duracion_llamada} min</p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isEmail ? (
                            resp.enviado ? (
                              <Badge className="bg-green-100 text-green-700">
                                <Check className="w-3 h-3 mr-1" /> Enviado
                              </Badge>
                            ) : (
                              <>
                                <Badge className="bg-red-100 text-red-700">Error</Badge>
                                <Button size="sm" variant="outline" onClick={() => handleReenviar(resp.id)}>
                                  <RefreshCw className="w-3 h-3 mr-1" /> Reenviar
                                </Button>
                              </>
                            )
                          ) : (
                            <Badge className={isTelefono ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}>
                              {isTelefono ? '📞 Teléfono' : '💬 WhatsApp'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap line-clamp-3">{resp.cuerpo}</p>
                      <p className="mt-2 text-xs text-gray-500">
                        {resp.fecha_envio ? formatDateTime(resp.fecha_envio) : formatDateTime(resp.created_at)} • {resp.enviado_por_nombre}
                      </p>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Botones de Respuesta */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Button 
              className={`${config.bgColor} hover:opacity-90 text-white`}
              onClick={() => { setShowRespuestaForm(true); setShowRespuestaGestionForm(false); }}
              disabled={!consulta.cliente_email}
            >
              <Mail className="w-4 h-4 mr-2" />
              {consulta.cliente_email ? 'Responder por Email' : 'Sin email'}
            </Button>
            
            <Button 
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => { 
                setShowRespuestaGestionForm(true); 
                setShowRespuestaForm(false); 
                setRespuestaGestionForm(prev => ({ ...prev, tipo_respuesta: 'telefono' }));
              }}
            >
              <Phone className="w-4 h-4 mr-2" />
              Registrar Llamada
            </Button>
            
            <Button 
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => { 
                setShowRespuestaGestionForm(true); 
                setShowRespuestaForm(false); 
                setRespuestaGestionForm(prev => ({ ...prev, tipo_respuesta: 'whatsapp' }));
              }}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Registrar WhatsApp
            </Button>
          </div>

          {/* Formulario de respuesta por Email */}
          {showRespuestaForm && (
            <Card className="border-2 border-cyan-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Nueva Respuesta
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {plantillas.length > 0 && (
                  <div>
                    <Label>Usar plantilla</Label>
                    <Select onValueChange={handlePlantillaSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar plantilla (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {plantillas.map((p) => (
                          <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label>Email destinatario</Label>
                  <Input
                    type="email"
                    value={respuestaForm.destinatario_email}
                    onChange={(e) => setRespuestaForm({...respuestaForm, destinatario_email: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Asunto</Label>
                  <Input
                    value={respuestaForm.asunto}
                    onChange={(e) => setRespuestaForm({...respuestaForm, asunto: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Respuesta</Label>
                  <Textarea
                    value={respuestaForm.cuerpo}
                    onChange={(e) => setRespuestaForm({...respuestaForm, cuerpo: e.target.value})}
                    rows={6}
                    placeholder="Escriba la respuesta al cliente..."
                  />
                </div>

                {/* Adjuntos */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Adjuntos
                  </Label>
                  <div className="mt-2 space-y-2">
                    {adjuntosDisponibles.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {adjuntosDisponibles.map((adj, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => toggleAdjunto(adj.path)}
                            className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 transition-colors ${
                              respuestaForm.adjuntos.includes(adj.path) 
                                ? 'bg-cyan-100 text-cyan-700 border-cyan-300 border' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <Paperclip className="w-3 h-3" />
                            {adj.nombre.split('_').slice(2).join('_') || adj.nombre}
                            {respuestaForm.adjuntos.includes(adj.path) && <Check className="w-3 h-3" />}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                        <Upload className="w-4 h-4" />
                        Subir nuevo archivo
                        <input type="file" className="hidden" onChange={handleUploadAdjunto} disabled={uploading} />
                      </label>
                      {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
                    </div>
                    {respuestaForm.adjuntos.length > 0 && (
                      <p className="text-xs text-cyan-600">{respuestaForm.adjuntos.length} archivo(s) seleccionado(s) para adjuntar</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowRespuestaForm(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    className={`${config.bgColor} hover:opacity-90 text-white`}
                    onClick={handleEnviarRespuesta}
                    disabled={enviando}
                  >
                    {enviando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Enviar Email {respuestaForm.adjuntos.length > 0 && `(${respuestaForm.adjuntos.length} adj.)`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formulario de Gestión por Teléfono/WhatsApp */}
          {showRespuestaGestionForm && (
            <Card className={`border-2 ${respuestaGestionForm.tipo_respuesta === 'telefono' ? 'border-blue-200' : 'border-emerald-200'}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  {respuestaGestionForm.tipo_respuesta === 'telefono' 
                    ? <><Phone className="w-4 h-4 text-blue-600" /> Registrar Llamada Telefónica</>
                    : <><MessageSquare className="w-4 h-4 text-emerald-600" /> Registrar Gestión WhatsApp</>
                  }
                </CardTitle>
                <p className="text-xs text-gray-500">
                  Registre los detalles de la gestión realizada con el cliente
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={respuestaGestionForm.tipo_respuesta === 'telefono' ? 'default' : 'outline'}
                    onClick={() => setRespuestaGestionForm(prev => ({ ...prev, tipo_respuesta: 'telefono' }))}
                    className={respuestaGestionForm.tipo_respuesta === 'telefono' ? 'bg-blue-600' : ''}
                  >
                    <Phone className="w-4 h-4 mr-1" /> Teléfono
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={respuestaGestionForm.tipo_respuesta === 'whatsapp' ? 'default' : 'outline'}
                    onClick={() => setRespuestaGestionForm(prev => ({ ...prev, tipo_respuesta: 'whatsapp' }))}
                    className={respuestaGestionForm.tipo_respuesta === 'whatsapp' ? 'bg-emerald-600' : ''}
                  >
                    <MessageSquare className="w-4 h-4 mr-1" /> WhatsApp
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Teléfono contactado</Label>
                    <Input
                      value={respuestaGestionForm.telefono_contacto}
                      onChange={(e) => setRespuestaGestionForm({...respuestaGestionForm, telefono_contacto: e.target.value})}
                      placeholder="Ej: 612345678"
                    />
                  </div>
                  {respuestaGestionForm.tipo_respuesta === 'telefono' && (
                    <div>
                      <Label>Duración (minutos)</Label>
                      <Input
                        type="number"
                        value={respuestaGestionForm.duracion_llamada}
                        onChange={(e) => setRespuestaGestionForm({...respuestaGestionForm, duracion_llamada: e.target.value})}
                        placeholder="Ej: 15"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <Label>Notas de la gestión *</Label>
                  <Textarea
                    value={respuestaGestionForm.notas_gestion}
                    onChange={(e) => setRespuestaGestionForm({...respuestaGestionForm, notas_gestion: e.target.value})}
                    rows={5}
                    placeholder={respuestaGestionForm.tipo_respuesta === 'telefono' 
                      ? "Describa el contenido de la llamada, acuerdos alcanzados, próximos pasos..."
                      : "Describa el contenido del mensaje de WhatsApp, respuesta del cliente..."
                    }
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowRespuestaGestionForm(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    className={respuestaGestionForm.tipo_respuesta === 'telefono' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                    onClick={handleRespuestaGestion}
                    disabled={enviando}
                  >
                    {enviando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {respuestaGestionForm.tipo_respuesta === 'telefono' 
                      ? <><Phone className="w-4 h-4 mr-2" /> Guardar Llamada</>
                      : <><MessageSquare className="w-4 h-4 mr-2" /> Guardar WhatsApp</>
                    }
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Botones de acción adicionales */}
          <div className="flex flex-wrap gap-2">
            {/* Botón Seguimiento */}
            <Button 
              variant="outline" 
              onClick={() => setShowSeguimientoForm(!showSeguimientoForm)}
              className="flex-1"
            >
              <GitBranch className="w-4 h-4 mr-2" />
              {showSeguimientoForm ? 'Cancelar' : 'Nueva Consulta de Seguimiento'}
            </Button>
            
            {/* Botón Finalizar Consulta */}
            {consulta.estado !== 'cerrado' && (
              <Button 
                variant="outline" 
                onClick={() => setShowFinalizarConsulta(!showFinalizarConsulta)}
                className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {showFinalizarConsulta ? 'Cancelar' : 'Finalizar Consulta'}
              </Button>
            )}
            
            {/* Botón Crear Actuación (solo si no finalizamos con actuación automática) */}
            {consulta.expediente_id && !consulta.actuacion_creada && consulta.estado !== 'cerrado' && (
              <Button 
                variant="outline" 
                onClick={() => setShowCrearActuacion(!showCrearActuacion)}
                className="flex-1"
              >
                <FolderPlus className="w-4 h-4 mr-2" />
                Crear Actuación
              </Button>
            )}
            
            {consulta.actuacion_creada && (
              <Badge className="bg-green-100 text-green-700">
                <Check className="w-3 h-3 mr-1" /> Actuación creada
              </Badge>
            )}
            
            {consulta.estado === 'cerrado' && (
              <Badge className="bg-gray-100 text-gray-700">
                <CheckCircle className="w-3 h-3 mr-1" /> Consulta cerrada
              </Badge>
            )}
          </div>

          {/* Formulario Finalizar Consulta */}
          {showFinalizarConsulta && (
            <Card className="border-2 border-red-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-red-700">
                  <CheckCircle className="w-4 h-4" />
                  Finalizar Consulta
                </CardTitle>
                <p className="text-xs text-gray-500">
                  Marcar esta consulta como resuelta y cerrada
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {consulta.expediente_id && !consulta.actuacion_creada && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded">
                    <input
                      type="checkbox"
                      id="crear_actuacion_auto"
                      checked={finalizarForm.crear_actuacion}
                      onChange={(e) => setFinalizarForm({...finalizarForm, crear_actuacion: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <label htmlFor="crear_actuacion_auto" className="text-sm">
                      <span className="font-medium">Crear actuación automática</span> en el expediente {consulta.expediente_numero}
                    </label>
                  </div>
                )}

                <div>
                  <Label>Notas de cierre (opcional)</Label>
                  <Textarea
                    value={finalizarForm.notas_cierre}
                    onChange={(e) => setFinalizarForm({...finalizarForm, notas_cierre: e.target.value})}
                    rows={3}
                    placeholder="Añada notas sobre la resolución de esta consulta..."
                  />
                </div>

                <div className="bg-amber-50 p-3 rounded text-sm text-amber-800">
                  <p className="font-medium">Al finalizar:</p>
                  <ul className="list-disc list-inside mt-1 text-xs">
                    <li>El estado de la consulta cambiará a &quot;Cerrado&quot;</li>
                    {finalizarForm.crear_actuacion && consulta.expediente_id && (
                      <li>Se creará una actuación con el resumen completo de la tramitación</li>
                    )}
                  </ul>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowFinalizarConsulta(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={handleFinalizarConsulta}
                    disabled={enviando}
                  >
                    {enviando && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Finalizar y Cerrar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formulario Seguimiento */}
          {showSeguimientoForm && (
            <Card className="border-2 border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <GitBranch className="w-4 h-4" />
                  Nueva Consulta de Seguimiento
                </CardTitle>
                <p className="text-xs text-gray-500">
                  Esta consulta quedará vinculada al hilo de tramitación original
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Origen</Label>
                  <div className="flex gap-2 mt-1">
                    {Object.entries(ORIGENES).map(([key, val]) => {
                      const OrIcon = val.icon;
                      return (
                        <Button
                          key={key}
                          type="button"
                          size="sm"
                          variant={seguimientoForm.origen === key ? 'default' : 'outline'}
                          onClick={() => setSeguimientoForm({...seguimientoForm, origen: key})}
                        >
                          <OrIcon className="w-4 h-4 mr-1" />
                          {val.nombre}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <Label>Asunto de la nueva consulta</Label>
                  <Input
                    value={seguimientoForm.asunto}
                    onChange={(e) => setSeguimientoForm({...seguimientoForm, asunto: e.target.value})}
                    placeholder="Ej: Seguimiento - Información adicional solicitada"
                  />
                </div>
                
                <div>
                  <Label>Descripción</Label>
                  <Textarea
                    value={seguimientoForm.descripcion_consulta}
                    onChange={(e) => setSeguimientoForm({...seguimientoForm, descripcion_consulta: e.target.value})}
                    rows={4}
                    placeholder="Detalle de la nueva consulta del cliente..."
                  />
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowSeguimientoForm(false)}>Cancelar</Button>
                  <Button 
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={handleCrearSeguimiento}
                    disabled={enviando}
                  >
                    {enviando && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    <GitBranch className="w-4 h-4 mr-2" />
                    Crear Seguimiento
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formulario Crear Actuación */}
          {showCrearActuacion && (
            <Card className="border-2 border-green-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FolderPlus className="w-4 h-4" />
                  Crear Actuación en Expediente
                </CardTitle>
                <p className="text-xs text-gray-500">
                  Se creará una actuación en el expediente {consulta.expediente_numero} con el resumen de toda la tramitación
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Título de la actuación (opcional)</Label>
                  <Input
                    value={crearActuacionForm.titulo}
                    onChange={(e) => setCrearActuacionForm({...crearActuacionForm, titulo: e.target.value})}
                    placeholder={`Atención al cliente: ${consulta.asunto?.substring(0, 50)}...`}
                  />
                </div>
                
                <div>
                  <Label>Observaciones adicionales (opcional)</Label>
                  <Textarea
                    value={crearActuacionForm.observaciones_adicionales}
                    onChange={(e) => setCrearActuacionForm({...crearActuacionForm, observaciones_adicionales: e.target.value})}
                    rows={3}
                    placeholder="Añada observaciones adicionales si lo desea..."
                  />
                </div>
                
                <div className="bg-amber-50 p-3 rounded text-sm text-amber-800">
                  <p className="font-medium">La actuación incluirá:</p>
                  <ul className="list-disc list-inside mt-1 text-xs">
                    <li>Datos del cliente</li>
                    <li>Consulta original y descripción</li>
                    <li>Todas las respuestas enviadas ({consulta.respuestas?.length || 0})</li>
                    <li>Todos los seguimientos ({consulta.seguimientos?.length || 0})</li>
                  </ul>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowCrearActuacion(false)}>Cancelar</Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={handleCrearActuacion}
                    disabled={enviando}
                  >
                    {enviando && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    <FolderPlus className="w-4 h-4 mr-2" />
                    Crear Actuación
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Información del hilo */}
          {(consulta.consulta_padre_id || (consulta.seguimientos && consulta.seguimientos.length > 0)) && (
            <Card className="bg-purple-50 border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Hilo de Tramitación
                </CardTitle>
              </CardHeader>
              <CardContent>
                {consulta.consulta_padre && (
                  <div className="mb-3 p-2 bg-white rounded">
                    <p className="text-sm font-medium">Consulta original:</p>
                    <p className="text-sm text-gray-600">{consulta.consulta_padre.asunto}</p>
                  </div>
                )}
                
                {consulta.seguimientos && consulta.seguimientos.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Seguimientos ({consulta.seguimientos.length}):</p>
                    {consulta.seguimientos.map((seg) => (
                      <div key={seg.id} className="p-2 bg-white rounded text-sm">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-purple-100 text-purple-700">#{seg.numero_seguimiento}</Badge>
                          <span className="font-medium">{seg.asunto}</span>
                          <Badge className={ESTADOS_CONSULTA[seg.estado]?.color || ''}>{seg.estado}</Badge>
                        </div>
                        <p className="text-gray-600 text-xs mt-1">{formatDateTime(seg.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// COMPONENTE: SubTareasSection - Sub-tareas/Sub-operaciones de una tarea
// ============================================================================
const SubTareasSection = ({ tipoTarea, tareaId, isExpanded, onToggle }) => {
  const [subtareas, setSubtareas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newSubtarea, setNewSubtarea] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isExpanded && tareaId) {
      loadSubtareas();
    }
  }, [isExpanded, tareaId, tipoTarea]);

  const loadSubtareas = async () => {
    setLoading(true);
    try {
      const res = await tareasDespachoAPI.subtareas.getByTarea(tipoTarea, tareaId);
      setSubtareas(res.data);
    } catch (error) {
      console.error('Error cargando sub-tareas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubtarea = async () => {
    if (!newSubtarea.trim()) return;
    
    setSaving(true);
    try {
      await tareasDespachoAPI.subtareas.create({
        tipo_tarea_padre: tipoTarea,
        tarea_padre_id: tareaId,
        descripcion: newSubtarea.trim(),
        orden: subtareas.length
      });
      setNewSubtarea('');
      loadSubtareas();
      toast({ title: 'Sub-tarea añadida' });
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo crear la sub-tarea', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSubtarea = async (subtarea) => {
    try {
      await tareasDespachoAPI.subtareas.completar(subtarea.id, !subtarea.completada);
      loadSubtareas();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleDeleteSubtarea = async (subtareaId) => {
    if (!window.confirm('¿Eliminar esta sub-tarea?')) return;
    try {
      await tareasDespachoAPI.subtareas.delete(subtareaId);
      loadSubtareas();
      toast({ title: 'Sub-tarea eliminada' });
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const completadas = subtareas.filter(st => st.completada).length;
  const total = subtareas.length;

  return (
    <div className="mt-2">
      <button 
        onClick={onToggle}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
      >
        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        <ListTodo className="w-4 h-4" />
        <span>Sub-tareas</span>
        {total > 0 && (
          <Badge className={completadas === total ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
            {completadas}/{total}
          </Badge>
        )}
      </button>

      {isExpanded && (
        <div className="mt-3 ml-6 space-y-2 border-l-2 border-gray-200 pl-4">
          {loading ? (
            <div className="py-2 flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Cargando...
            </div>
          ) : (
            <>
              {subtareas.map((st) => (
                <div key={st.id} className="flex items-center gap-2 group">
                  <button 
                    onClick={() => handleToggleSubtarea(st)}
                    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                      st.completada 
                        ? 'bg-green-500 border-green-500 text-white' 
                        : 'border-gray-300 hover:border-green-500'
                    }`}
                  >
                    {st.completada && <Check className="w-3 h-3" />}
                  </button>
                  <span className={`flex-1 text-sm ${st.completada ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                    {st.descripcion}
                  </span>
                  <button 
                    onClick={() => handleDeleteSubtarea(st.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-opacity"
                  >
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ))}

              <div className="flex items-center gap-2 mt-2">
                <input
                  type="text"
                  placeholder="Nueva sub-tarea..."
                  className="flex-1 text-sm border-0 border-b border-gray-200 focus:border-blue-500 outline-none py-1"
                  value={newSubtarea}
                  onChange={(e) => setNewSubtarea(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSubtarea()}
                />
                <button 
                  onClick={handleAddSubtarea}
                  disabled={saving || !newSubtarea.trim()}
                  className="p-1 hover:bg-blue-100 rounded disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 text-blue-600" />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TareasDespacho;
